<template>
    <!-- Opened from a page on a wide screen: a side panel on the right (the page stays readable while reviewing the draft,
         Keelan 3 Nov). Narrow screens keep the pop-up; the Forge page shows it in place (not modal). -->
    <div v-if="actions.length" :class="modal ? (docked ? 'ft-dock-wrap' : 'error-msg-wrap') : 'ft-host'" :style="docked ? { top: dockTop + 'px' } : undefined">
    <section class="ft" :class="{ 'form-wrapper slide-up ft-modal': modal && !docked, 'ft-docked': docked }" :role="modal ? (docked ? 'complementary' : 'dialog') : null" :aria-label="modal ? 'The Forge' : null">
        <button v-if="modal" type="button" class="ft-close" title="Close" :disabled="busy" @click="$emit('close')">×</button>
        <div class="ft-head">
            <div class="ft-icon"><svg-icon icon="twousers" /></div>
            <div>
                <h2>{{ modal ? 'The Forge' : single ? familyLabel : 'Forge for your team' }}</h2>
                <p>Describe it, check the draft, then add it. Nothing is saved or sent until you say so.</p>
            </div>
        </div>

        <!-- Forge page (single): one tool at a time; Scorecard and Rocks carry their improve / milestones versions here -->
        <div v-if="single && variants.length > 1" class="ft-variants" role="tablist" :aria-label="familyLabel">
            <button v-for="v in variants" :key="v.key" type="button" role="tab" :aria-selected="kind == v.key" class="ft-variant" :class="{ 'ft-variant-on': kind == v.key }" :disabled="busy" @click="kind != v.key && pick(v.key)">{{ v.label }}</button>
        </div>

        <div v-if="actions.length > 1 && !single" class="ft-kinds" role="tablist">
            <button v-for="a in actions" :key="a.key" type="button" role="tab" :aria-selected="kind == a.key" class="ft-kind" :class="{ 'ft-kind-on': kind == a.key }" :disabled="busy" @click="pick(a.key)">
                <span class="ft-kind-name">{{ a.label }}</span>
                <span class="ft-kind-sub">{{ a.sub }}</span>
            </button>
        </div>

        <div v-if="loadError" class="ft-error" role="alert">{{ loadError }}</div>

        <!-- DESCRIBE -->
        <template v-if="kind && phase == 'input'">
            <div v-if="kind == 'scorecard' || usesRocks" class="ft-row">
                <label>Team
                    <select v-model="team">
                        <option v-for="t in teamOptions" :key="t" :value="t">{{ teamLabel(t) }}</option>
                    </select>
                </label>
                <label v-if="usesRocks">Quarter
                    <select v-model="quarter">
                        <option v-for="q in quarterOptions" :key="q" :value="q">{{ quarterLabel(q) }}{{ q == quarterOptions[0] ? ' · now' : ' · next' }}</option>
                    </select>
                </label>
            </div>
            <div v-if="kind == 'skills'" class="ft-row">
                <label>Matrix
                    <select v-model="matrixID">
                        <option :value="null">Let the Forge pick, or start a new one</option>
                        <option v-for="m in matrices" :key="m.ID" :value="m.ID">{{ m.Name }}</option>
                    </select>
                </label>
            </div>
            <div v-if="picksRocks && ready" class="ft-list ft-rockpick">
                <label class="ft-label">Rocks to {{ kind == 'improve' ? 'improve' : 'plan' }} · {{ pickedRocks.length }} of {{ editableRocks.length }}</label>
                <label v-for="r in editableRocks" :key="r.ID" class="ft-item ft-item-tight">
                    <input type="checkbox" :value="r.ID" v-model="pickedRocks" :aria-label="(kind == 'improve' ? 'Improve ' : 'Plan ') + r.Name" />
                    <span class="ft-rockpick-name">{{ r.Name }}</span>
                    <span v-if="kind == 'improve'" class="ft-hint">{{ r.DoneStatement ? '' : 'no done statement yet' }}</span>
                    <span v-else class="ft-hint">{{ r.Milestones.length ? r.Milestones.length + ' milestone' + (r.Milestones.length == 1 ? '' : 's') : 'no milestones yet' }}</span>
                </label>
                <div v-if="!editableRocks.length" class="ft-hint">No open Rocks you can edit on {{ teamLabel(team) }} for {{ quarterLabel(quarter) }}. Add some with Rocks first.</div>
            </div>
            <div v-if="kind == 'improvemetrics' && ready" class="ft-list ft-rockpick">
                <label class="ft-label">Metrics to improve · {{ pickedMetrics.length }} of {{ teamMetrics.length }}</label>
                <label v-for="m in teamMetrics" :key="m.ID" class="ft-item ft-item-tight">
                    <input type="checkbox" :value="m.ID" v-model="pickedMetrics" :aria-label="'Improve ' + m.Name" />
                    <span class="ft-rockpick-name">{{ m.Name }}</span>
                    <span class="ft-hint">{{ [ m.Definition ? '' : 'no definition', m.OwnerEmployeeID ? '' : 'no owner' ].filter(Boolean).join(' · ') }}</span>
                </label>
                <div v-if="!teamMetrics.length" class="ft-hint">{{ teamLabel(team) }} has no metrics yet. Add some with Scorecard first.</div>
            </div>
            <div v-if="forMeeting && ready" class="ft-facts">
                <div class="ft-label">{{ kind == 'prepl10' ? 'The Forge will look at' : 'From ' + meeting.name }}</div>
                <ul v-if="kind == 'prepl10'">
                    <li v-if="meeting.team"><b>{{ facts.offTrackRocks.length }}</b> Rock{{ facts.offTrackRocks.length == 1 ? '' : 's' }} off track or behind on milestones</li>
                    <li v-if="meeting.team"><b>{{ facts.scorecard.length }}</b> number{{ facts.scorecard.length == 1 ? '' : 's' }} off goal 2+ weeks or not entered last week</li>
                    <li><b>{{ facts.overdueTodos.length }}</b> overdue to-do{{ facts.overdueTodos.length == 1 ? '' : 's' }}</li>
                    <li><b>{{ facts.existingIssues.length }}</b> Issue{{ facts.existingIssues.length == 1 ? '' : 's' }} already open (it won't repeat them)</li>
                </ul>
                <div v-else class="ft-stats">
                    <div v-for="t in statTiles" :key="t.label" class="ft-stat"><strong>{{ t.value }}</strong><span>{{ t.label }}</span></div>
                </div>
                <div v-if="kind == 'prepl10' && !meeting.team" class="ft-hint">This is a personal meeting, so only its to-dos are checked. Team meetings also bring in the team's Rocks and scorecard.</div>
            </div>
            <div v-if="forBoard && ready" class="ft-facts">
                <div class="ft-label">From {{ board.name }}</div>
                <ul>
                    <template v-if="kind == 'sortinbox'"><li><b>{{ inbox.length }}</b> item{{ inbox.length == 1 ? '' : 's' }} in your Inbox</li></template>
                    <template v-else>
                        <li><b>{{ boardFacts.open.length }}</b> open card{{ boardFacts.open.length == 1 ? '' : 's' }}<template v-if="kind == 'tidyboard'"> · <b>{{ boardFacts.overdue }}</b> overdue · <b>{{ boardFacts.stale }}</b> unchanged for 3+ weeks · <b>{{ boardFacts.undated + boardFacts.steps.length }}</b> with no due date</template><template v-else> · <b>{{ boardFacts.dueThisWeek }}</b> due by Sunday</template></li>
                        <li v-if="kind == 'planweek'"><b>{{ myRocks.length }}</b> Rock{{ myRocks.length == 1 ? '' : 's' }} you own this quarter</li>
                    </template>
                </ul>
                <div v-if="kind == 'sortinbox' && !inbox.length" class="ft-hint">Your Inbox is empty. Text a note to your Inbox number, or drag a card into the Inbox panel.</div>
            </div>
            <div class="ft-describe">
                <textarea v-model="description" rows="4" :placeholder="current.placeholder" maxlength="8000"></textarea>
                <ai-dictation class="ft-mic" @text="t => description = (description ? description.trimEnd() + ' ' : '') + t" />
            </div>
            <div class="ft-actions">
                <span class="ft-hint">{{ current.hint }}</span>
                <div class="ai-btn" :class="{ 'disable-button': !canDraft }" @click="draftIt()">{{ ready ? 'Draft it' : 'Loading…' }}</div>
            </div>
        </template>

        <div v-if="phase == 'loading'" class="ft-loading" role="status"><div class="ft-orb"></div>{{ loadingText }}</div>

        <!-- REVIEW -->
        <template v-if="phase == 'review' && draft">
            <p v-if="draft.summary" class="ft-summary">{{ draft.summary }}</p>

            <!-- Scorecard metrics -->
            <div v-if="kind == 'scorecard'" class="ft-list">
                <div class="ft-meta">For <strong>{{ teamLabel(team) }}</strong> · goals start this week</div>
                <div v-for="(m, i) in draft.metrics" :key="i" class="ft-item" :class="{ 'ft-off': !m.keep }">
                    <input type="checkbox" v-model="m.keep" :aria-label="'Add ' + m.name" />
                    <div class="ft-item-body">
                        <div class="ft-grid ft-grid-metric">
                            <input type="text" v-model="m.name" maxlength="80" aria-label="Metric name" class="ft-strong" />
                            <select v-model="m.ownerEmployeeID" aria-label="Owner"><option :value="null">No owner yet</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                            <select v-model="m.cmp" aria-label="Goal type"><option value="gte">At least</option><option value="lte">At most</option><option value="eq">Exactly</option><option value="range">Between</option></select>
                            <input type="number" v-model.number="m.goal" step="any" aria-label="Goal" />
                            <input v-if="m.cmp == 'range'" type="number" v-model.number="m.goalMax" step="any" aria-label="Goal maximum" placeholder="and" />
                            <input type="text" v-model="m.unit" maxlength="12" aria-label="Unit" placeholder="unit" class="ft-unit" />
                        </div>
                        <input type="text" v-model="m.definition" aria-label="Definition" placeholder="What counts, and where the number comes from" />
                        <div v-if="m.why" class="ft-why">{{ m.why }}</div>
                    </div>
                </div>
            </div>

            <!-- Rocks -->
            <div v-if="kind == 'rocks'" class="ft-list">
                <div class="ft-meta">For <strong>{{ teamLabel(team) }}</strong> · {{ quarterLabel(quarter) }} ({{ shortDate(bounds.start) }} – {{ shortDate(bounds.end) }})</div>
                <div v-for="(r, i) in draft.rocks" :key="i" class="ft-item" :class="{ 'ft-off': !r.keep }">
                    <input type="checkbox" v-model="r.keep" :aria-label="'Add ' + r.name" />
                    <div class="ft-item-body">
                        <div class="ft-grid ft-grid-rock">
                            <input type="text" v-model="r.name" maxlength="200" aria-label="Rock" class="ft-strong" />
                            <select v-model="r.ownerEmployeeID" aria-label="Owner"><option :value="null">No owner yet</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                        </div>
                        <label class="ft-label">Done looks like</label>
                        <textarea v-model="r.doneStatement" rows="2" maxlength="1000" class="ft-fit"></textarea>
                        <label class="ft-check"><input type="checkbox" v-model="r.teamRock" /> Team Rock (the whole team owns it)</label>
                        <label class="ft-label">Milestones</label>
                        <div v-for="(ms, j) in r.milestones" :key="j" class="ft-grid ft-grid-ms">
                            <textarea v-model="ms.text" rows="1" aria-label="Milestone" class="ft-ms-text"></textarea>
                            <select v-model="ms.ownerEmployeeID" aria-label="Milestone owner"><option :value="null">—</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                            <input type="date" v-model="ms.dueDate" :min="bounds.start" :max="bounds.end" aria-label="Due" />
                            <button type="button" class="ft-x" title="Remove milestone" @click="r.milestones.splice(j, 1)">×</button>
                        </div>
                        <a class="ft-add" @click.prevent="r.milestones.push({ text: '', ownerEmployeeID: r.ownerEmployeeID, dueDate: bounds.end })">+ Add a milestone</a>
                    </div>
                </div>
            </div>

            <!-- Milestones for existing rocks -->
            <div v-if="kind == 'milestones'" class="ft-list">
                <div class="ft-meta">For <strong>{{ teamLabel(team) }}</strong> · {{ quarterLabel(quarter) }} · due by {{ shortDate(bounds.end) }}</div>
                <div v-for="r in draft.rocks" :key="r.rockID" class="ft-item" :class="{ 'ft-off': !r.keep }">
                    <input type="checkbox" v-model="r.keep" :aria-label="'Add milestones to ' + r.name" />
                    <div class="ft-item-body">
                        <div class="ft-strong">{{ r.name }}</div>
                        <div v-if="r.doneStatement" class="ft-hint">Done looks like: {{ r.doneStatement }}</div>
                        <template v-if="r.existing.length">
                            <label class="ft-label">Already on it</label>
                            <div v-for="(ms, j) in r.existing" :key="'e' + j" class="ft-existing" :class="{ 'ft-existing-done': ms.Done }">{{ ms.Done ? '✓ ' : '' }}{{ ms.Text }}<span v-if="ms.DueDate"> · {{ shortDate(ms.DueDate) }}</span></div>
                        </template>
                        <label class="ft-label">New milestones</label>
                        <div v-for="(ms, j) in r.milestones" :key="j" class="ft-grid ft-grid-ms">
                            <textarea v-model="ms.text" rows="1" aria-label="Milestone" class="ft-ms-text"></textarea>
                            <select v-model="ms.ownerEmployeeID" aria-label="Milestone owner"><option :value="null">—</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                            <input type="date" v-model="ms.dueDate" :min="earliest" :max="bounds.end" aria-label="Due" />
                            <button type="button" class="ft-x" title="Remove milestone" @click="r.milestones.splice(j, 1)">×</button>
                        </div>
                        <div v-if="!r.milestones.length" class="ft-hint">Nothing new suggested; it already looks planned.</div>
                        <a class="ft-add" @click.prevent="r.milestones.push({ text: '', ownerEmployeeID: r.ownerEmployeeID, dueDate: bounds.end })">+ Add a milestone</a>
                    </div>
                </div>
            </div>

            <!-- Clearer wording for existing rocks -->
            <div v-if="kind == 'improve'" class="ft-list">
                <div class="ft-meta">For <strong>{{ teamLabel(team) }}</strong> · {{ quarterLabel(quarter) }} · owners, status and milestones stay as they are</div>
                <div v-for="r in draft.rocks" :key="r.rockID" class="ft-item" :class="{ 'ft-off': !r.keep || !rockChanged(r) }">
                    <input type="checkbox" v-model="r.keep" :disabled="!rockChanged(r)" :aria-label="'Update ' + r.oldName" />
                    <div class="ft-item-body">
                        <label class="ft-label">Now</label>
                        <div class="ft-was">
                            <div class="ft-was-name">{{ r.oldName }}</div>
                            <div v-if="r.oldDone">Done: {{ r.oldDone }}</div>
                            <div v-else class="ft-hint">No done statement yet</div>
                        </div>
                        <label class="ft-label">Suggested</label>
                        <input type="text" v-model="r.name" maxlength="200" aria-label="Rock" class="ft-strong" />
                        <label class="ft-label">Done looks like</label>
                        <textarea v-model="r.doneStatement" rows="2" maxlength="1000" class="ft-fit" aria-label="Done looks like"></textarea>
                        <div v-if="r.why" class="ft-why">{{ r.why }}</div>
                        <div v-if="!rockChanged(r)" class="ft-hint">No change: it already reads clearly.</div>
                    </div>
                </div>
            </div>

            <!-- Clearer wording for existing metrics -->
            <div v-if="kind == 'improvemetrics'" class="ft-list">
                <div class="ft-meta">For <strong>{{ teamLabel(team) }}</strong> · goals and past numbers stay as they are</div>
                <div v-for="m in draft.metrics" :key="m.metricID" class="ft-item" :class="{ 'ft-off': !m.keep || !metricChanged(m) }">
                    <input type="checkbox" v-model="m.keep" :disabled="!metricChanged(m)" :aria-label="'Update ' + m.old.name" />
                    <div class="ft-item-body">
                        <label class="ft-label">Now</label>
                        <div class="ft-was">
                            <div class="ft-was-name">{{ m.old.name }}<span v-if="m.old.unit"> ({{ m.old.unit }})</span> · goal {{ m.goalText }}</div>
                            <div v-if="m.old.definition">{{ m.old.definition }}</div>
                            <div v-else class="ft-hint">No definition yet</div>
                            <div class="ft-hint">{{ m.old.ownerEmployeeID ? 'Owner: ' + personName(m.old.ownerEmployeeID) : 'No owner yet' }}</div>
                        </div>
                        <label class="ft-label">Suggested</label>
                        <div class="ft-grid ft-grid-rock">
                            <input type="text" v-model="m.name" maxlength="80" aria-label="Metric name" class="ft-strong" />
                            <select v-model="m.ownerEmployeeID" aria-label="Owner"><option :value="null">No owner yet</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                        </div>
                        <label class="ft-label">Definition</label>
                        <textarea v-model="m.definition" rows="2" maxlength="1000" class="ft-fit" aria-label="Definition"></textarea>
                        <label class="ft-label">Unit</label>
                        <input type="text" v-model="m.unit" maxlength="12" aria-label="Unit" placeholder="none (a count)" class="ft-unit" />
                        <div v-if="m.why" class="ft-why">{{ m.why }}</div>
                        <div v-if="!metricChanged(m)" class="ft-hint">No change: it already reads clearly.</div>
                    </div>
                </div>
            </div>

            <!-- Issues drafted for a meeting -->
            <div v-if="kind == 'prepl10'" class="ft-list">
                <div class="ft-meta">Added to <strong>{{ facts.issuesColumn }}</strong> on {{ meeting.name }}</div>
                <div v-for="(it, i) in draft.issues" :key="i" class="ft-item" :class="{ 'ft-off': !it.keep }">
                    <input type="checkbox" v-model="it.keep" :aria-label="'Add ' + it.name" />
                    <div class="ft-item-body">
                        <div class="ft-grid ft-grid-rock">
                            <input type="text" v-model="it.name" maxlength="200" aria-label="Issue" class="ft-strong" />
                            <select v-model="it.assigneeEmployeeID" aria-label="Owner"><option :value="null">No owner</option><option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option></select>
                        </div>
                        <textarea v-model="it.description" rows="2" maxlength="2000" class="ft-fit" aria-label="Details"></textarea>
                        <div class="ft-row ft-row-tight">
                            <span class="ft-tag" :class="'ft-tag-' + it.source">{{ sourceLabel(it.source) }}</span>
                            <select v-model="it.priority" aria-label="Priority" class="ft-pri"><option value="high">High</option><option value="med">Medium</option><option value="low">Low</option></select>
                        </div>
                    </div>
                </div>
                <div v-if="!draft.issues.length" class="ft-hint">Nothing new to discuss: no new Issues suggested.</div>
            </div>

            <!-- How the meeting is going -->
            <div v-if="kind == 'meetingcheckup'" class="ft-list">
                <div class="ft-stats">
                    <div v-for="t in statTiles" :key="t.label" class="ft-stat"><strong>{{ t.value }}</strong><span>{{ t.label }}</span></div>
                </div>
                <label v-if="draft.suggestions.length" class="ft-label">Suggestions · tick any to add to {{ facts.issuesColumn }}</label>
                <label v-for="(sg, i) in draft.suggestions" :key="i" class="ft-item ft-item-tight">
                    <input type="checkbox" v-model="sg.keep" :aria-label="'Add ' + sg.title + ' to Issues'" />
                    <span class="ft-item-body"><span class="ft-strong">{{ sg.title }}</span><span class="ft-sg-detail">{{ sg.detail }}</span></span>
                </label>
            </div>

            <!-- Board tidy-up -->
            <div v-if="kind == 'tidyboard'" class="ft-list">
                <div v-if="dueActions.length > 1" class="ft-row ft-row-tight">
                    <span class="ft-hint">{{ dueActions.length }} due date{{ dueActions.length == 1 ? '' : 's' }} suggested ·</span>
                    <a class="ft-add" @click.prevent="dueActions.forEach(a => { a.keep = true; })">Tick all dates</a>
                    <a class="ft-add" @click.prevent="dueActions.forEach(a => { a.keep = false; })">Untick all dates</a>
                </div>
                <div v-for="(a, i) in draft.actions" :key="i" class="ft-item" :class="{ 'ft-off': !a.keep }">
                    <input type="checkbox" v-model="a.keep" :aria-label="actionLabel(a) + ': ' + (a.stepName || a.cardName)" />
                    <div class="ft-item-body">
                        <div class="ft-strong">{{ a.stepName || a.cardName }}</div>
                        <div v-if="a.stepName" class="ft-hint">Checklist step on {{ a.cardName }}<template v-if="a.card && a.card.Date"> · card due {{ shortDate(a.card.Date) }}</template></div>
                        <div class="ft-row ft-row-tight">
                            <span class="ft-tag" :class="'ft-tag-' + (a.action == 'rock' ? 'rock' : a.action == 'done' ? 'todo' : a.action == 'due' ? 'other' : 'scorecard')">{{ actionLabel(a) }}</span>
                            <select v-if="a.action == 'move'" v-model="a.toColumn" aria-label="Move to" class="ft-pri"><option v-for="c in board.columns" :key="c" :value="c">{{ c }}</option></select>
                            <input v-if="a.action == 'due'" type="date" v-model="a.dueDate" :min="todayDate" :max="a.maxDate || null" aria-label="Due date" class="ft-pri" />
                        </div>
                        <div class="ft-sg-detail">{{ a.reason }}</div>
                        <template v-if="a.action == 'rock'">
                            <label class="ft-label">Rock</label>
                            <input type="text" v-model="a.rockName" maxlength="200" aria-label="Rock" class="ft-strong" />
                            <label class="ft-label">Done looks like</label>
                            <textarea v-model="a.doneStatement" rows="2" maxlength="1000" class="ft-fit" aria-label="Done looks like"></textarea>
                        </template>
                    </div>
                </div>
                <div v-if="!draft.actions.length" class="ft-hint">The board already looks tidy and every card has a date: nothing to change.</div>
            </div>

            <!-- This week's focus -->
            <div v-if="kind == 'planweek'" class="ft-list">
                <div v-for="(p, i) in draft.picks" :key="p.cardID" class="ft-item" :class="{ 'ft-off': !p.keep }">
                    <input type="checkbox" v-model="p.keep" :aria-label="'Focus on ' + p.name" />
                    <div class="ft-item-body">
                        <div class="ft-strong">{{ i + 1 }}. {{ p.name }}</div>
                        <div class="ft-hint">{{ p.column }}<template v-if="p.due"> · due {{ shortDate(p.due) }}</template></div>
                        <div class="ft-sg-detail">{{ p.why }}</div>
                    </div>
                </div>
                <div class="ft-row ft-row-tight">
                    <label>Move the ticked cards to
                        <select v-model="draft.toColumn" aria-label="Move to column"><option value="">Don't move them</option><option v-for="c in board.columns" :key="c" :value="c">{{ c }}</option></select>
                    </label>
                </div>
            </div>

            <!-- Inbox sorted onto the board -->
            <div v-if="kind == 'sortinbox'" class="ft-list">
                <div v-for="it in draft.items" :key="it.inboxID" class="ft-item" :class="{ 'ft-off': !it.keep }">
                    <input type="checkbox" v-model="it.keep" :aria-label="'Add ' + it.name" />
                    <div class="ft-item-body">
                        <div class="ft-hint">From your Inbox: {{ it.original }}</div>
                        <input type="text" v-model="it.name" maxlength="255" aria-label="Card title" class="ft-strong" />
                        <div class="ft-row ft-row-tight">
                            <select v-model="it.column" aria-label="Column" class="ft-pri"><option v-for="c in board.columns" :key="c" :value="c">{{ c }}</option></select>
                            <input type="date" v-model="it.dueDate" aria-label="Due date" class="ft-pri" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Training session -->
            <div v-if="kind == 'training'" class="ft-list">
                <div class="ft-grid ft-grid-training">
                    <input type="text" v-model="draft.name" maxlength="200" aria-label="Session name" class="ft-strong" />
                    <select v-model="draft.type" aria-label="Session type"><option value="Classroom">Classroom</option><option value="On-site">On-site</option></select>
                    <input type="date" v-model="draft.date" aria-label="Date" />
                </div>
                <input v-if="draft.type == 'On-site'" type="text" v-model="draft.station" placeholder="Station, e.g. Expander Line 2" aria-label="Station" />
                <textarea v-model="draft.reviewingText" rows="2" placeholder="What to focus on (optional)" class="ft-fit"></textarea>
                <label class="ft-label">{{ draft.type == 'On-site' ? 'Related processes' : 'Processes to review' }} · {{ draft.processSlugs.length }}</label>
                <div class="ft-chips">
                    <span v-for="slug in draft.processSlugs" :key="slug" class="ft-chip">{{ nodeName(slug) }}<button type="button" class="ft-x" title="Remove" @click="draft.processSlugs = draft.processSlugs.filter(s => s != slug)">×</button></span>
                </div>
                <input type="text" v-model="processQ" placeholder="Add another process…" aria-label="Search processes" />
                <div v-if="processQ.trim().length > 1" class="ft-pick">
                    <div v-for="n in processMatches" :key="n.Slug" class="ft-pick-row" @click="draft.processSlugs.push(n.Slug); processQ = ''">{{ n.Name }}</div>
                    <div v-if="!processMatches.length" class="ft-hint">No match</div>
                </div>
                <div v-if="draft.why" class="ft-why">{{ draft.why }}</div>
                <div class="ft-hint">Attendees are picked when the session starts, as usual.</div>
            </div>

            <!-- Skills matrix -->
            <div v-if="kind == 'skills'" class="ft-list">
                <div class="ft-meta">
                    <template v-if="skillsTarget">Updating <strong>{{ skillsTarget.Name }}</strong></template>
                    <template v-else>New matrix: <input type="text" v-model="draft.newMatrixName" aria-label="New matrix name" class="ft-inline" /></template>
                </div>
                <template v-if="draft.addSkills.length">
                    <label class="ft-label">New skills</label>
                    <div class="ft-chips"><span v-for="(s, i) in draft.addSkills" :key="s" class="ft-chip">{{ s }}<button type="button" class="ft-x" @click="draft.addSkills.splice(i, 1)">×</button></span></div>
                </template>
                <template v-if="draft.addPeople.length">
                    <label class="ft-label">New people</label>
                    <div class="ft-chips"><span v-for="(p, i) in draft.addPeople" :key="p" class="ft-chip">{{ p }}<button type="button" class="ft-x" @click="draft.addPeople.splice(i, 1)">×</button></span></div>
                </template>
                <template v-if="draft.ratings.length">
                    <label class="ft-label">Ratings</label>
                    <div v-for="(r, i) in draft.ratings" :key="i" class="ft-item ft-item-tight" :class="{ 'ft-off': !r.keep }">
                        <input type="checkbox" v-model="r.keep" />
                        <div class="ft-grid ft-grid-rating">
                            <span class="ft-strong">{{ r.person }}</span><span>{{ r.skill }}</span>
                            <select v-model.number="r.level"><option v-for="(l, n) in LEVELS" :key="n" :value="n">{{ n }} · {{ l }}</option></select>
                        </div>
                    </div>
                </template>
                <div v-if="!draft.addSkills.length && !draft.addPeople.length && !draft.ratings.length" class="ft-hint">Nothing to change — say what to add or rate below.</div>
            </div>

            <!-- Announcement -->
            <div v-if="kind == 'announcement'" class="ft-list">
                <textarea v-model="draft.text" rows="4" maxlength="1000" class="ft-fit" aria-label="Announcement"></textarea>
                <div class="ft-hint">{{ draft.text.length }} characters · your name is added at the end</div>
                <label class="ft-label">Send to</label>
                <div class="ft-chips">
                    <label v-for="l in locations" :key="l" class="ft-check ft-loc"><input type="checkbox" :value="l" v-model="draft.locations" /> {{ l }}</label>
                </div>
            </div>

            <!-- Team event -->
            <div v-if="kind == 'event'" class="ft-list">
                <div class="ft-grid ft-grid-event">
                    <input type="text" v-model="draft.name" maxlength="200" aria-label="Event name" class="ft-strong" />
                    <input type="date" v-model="draft.date" aria-label="Date" />
                    <input type="time" v-model="draft.startTime" aria-label="Start time" />
                    <select v-model.number="draft.durationMinutes" aria-label="Length"><option :value="null">Length —</option><option v-for="d in DURATIONS" :key="d" :value="d">{{ durationLabel(d) }}</option></select>
                    <select v-model="draft.location" aria-label="Where"><option :value="null">Everyone</option><option v-for="l in locations" :key="l" :value="l">{{ l }}</option></select>
                </div>
                <textarea v-model="draft.description" rows="2" placeholder="What to expect" class="ft-fit"></textarea>
                <label class="ft-check"><input type="checkbox" v-model="draft.sendOut" /> Also send it out as an announcement</label>
                <template v-if="draft.sendOut">
                    <textarea v-model="draft.announcementText" rows="3" maxlength="1000" class="ft-fit" aria-label="Announcement text"></textarea>
                    <div class="ft-chips">
                        <label v-for="l in locations" :key="l" class="ft-check ft-loc"><input type="checkbox" :value="l" v-model="draft.sendLocations" /> {{ l }}</label>
                    </div>
                </template>
            </div>

            <div v-if="texting" class="ft-warn">Posting texts everyone at {{ sendLocations.join(' and ') }} right away. It can't be unsent.</div>
            <div v-if="applyError" class="ft-error" role="alert">{{ applyError }}</div>

            <div class="ft-revise">
                <textarea v-model="feedback" rows="2" :placeholder="'Want anything changed? e.g. ' + (kind == 'improve' ? 'the shipping target is 98% on time, keep Hiring\'s name as it is…' : kind == 'milestones' ? 'fewer steps, interviews done by mid-November…' : kind == 'prepl10' ? 'split the to-dos into separate Issues, make the shipping one high priority…' : kind == 'meetingcheckup' ? 'focus on how long the meeting runs…' : 'add a metric for safety incidents, make the goals weekly not monthly…')" class="ft-fit"></textarea>
                <div class="ft-actions">
                    <button type="button" class="ai-btn ai-btn-ghost" :disabled="!feedback.trim() || busy" @click="draftIt(true)">Revise</button>
                    <button type="button" class="ai-btn ai-btn-ghost" :disabled="busy" @click="startOver()">Start over</button>
                    <div class="ai-btn" :class="{ 'disable-button': !canApply || busy }" @click="apply()">{{ busy ? 'Saving…' : confirmTexts ? 'Yes, ' + applyLabel.toLowerCase() : applyLabel }}</div>
                </div>
            </div>
        </template>

        <!-- DONE -->
        <div v-if="phase == 'done'" class="ft-done">
            <div class="ft-done-msg">{{ doneText }}</div>
            <div v-if="doneProblems.length" class="ft-error">{{ doneProblems.join(' ') }}</div>
            <div class="ft-actions">
                <button v-if="modal" type="button" class="ai-btn" @click="$emit('close')">Close</button>
                <router-link v-else-if="doneLink" :to="doneLink" class="ai-btn">{{ doneLinkLabel }}</router-link>
                <button type="button" class="ai-btn ai-btn-ghost" @click="startOver()">Make another</button>
            </div>
        </div>
    </section>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from './SvgIcon.vue';
import AIDictation from './AIDictation.vue';
import { mondayOf, goalAt, goalText, recentWeeks, offStreak, dueWeekOf } from '@/scoreboard';
import { lateMilestones, isRockOwner, rockTeamsOf } from '@/rocks';
import { htmlToText } from '@/inboxText';
import { browserTimeZone } from '@/meetingTime';

const FT_DOCK_MIN_WIDTH = 1100; // its drafts (metrics, rocks, milestones) need more room than the editors' panel

// The Forge for team tools (API routes/ai.ts /team-draft, services/aiteam.ts): scorecard metrics, rocks, milestones or clearer wording for existing rocks, a training
// session, skills matrix updates, an announcement or a team event, drafted from a description. The draft is
// checked here against what the person's own pages show them, edited in place, then applied through each tool's
// own routes (scoreboard.ts, rocks.ts, classroom.ts, skillsmatrices.ts, announcements.ts, teamevents.ts), so
// their permission checks still decide. Only the actions the person can use are offered.
const COMPANY_TEAM = '__company__';
const LEVELS = [ 'No Competence', 'Low Competence', 'Some Competence', 'High Competence', 'Expert' ];
const DURATIONS = [ 30, 60, 90, 120, 180, 240, 480 ];

function localDate(d: Date = new Date()): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function quarterOf(d: Date): string {
    return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}
function nextQuarterOf(q: string): string {
    const [ y, n ] = [ Number(q.slice(0, 4)), Number(q.slice(6)) ];
    return n == 4 ? `${y + 1}-Q1` : `${y}-Q${n + 1}`;
}
function quarterBounds(q: string): { start: string, end: string } {
    const y = Number(q.slice(0, 4)), n = Number(q.slice(6));
    const start = new Date(y, (n - 1) * 3, 1), end = new Date(y, n * 3, 0);
    return { start: localDate(start), end: localDate(end) };
}
const validDate = (s: any) => typeof s == 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s + 'T00:00:00Z'));

// Versions of one tool, shown as a switch inside it on the Forge page (they used to be separate tools in the menu)
const FAMILIES: { [k: string]: { key: string, label: string }[] } = {
    scorecard: [ { key: 'scorecard', label: 'New numbers' }, { key: 'improvemetrics', label: 'Sharpen existing' } ],
    rocks: [ { key: 'rocks', label: 'New Rocks' }, { key: 'milestones', label: 'Add milestones' }, { key: 'improve', label: 'Sharpen existing' } ]
};

export default defineComponent({
    name: 'ForgeTeam',
    components: { SvgIcon, AiDictation: AIDictation },
    // On the Forge page it's a card with every action. modal: a pop-up opened from another page (RocksView, ScorecardView)
    // with just the actions in `only`, starting on startKind for presetTeam / presetQuarter; it emits applied after a
    // save (so that page reloads) and close.
    props: {
        modal: { type: Boolean, default: false },
        // The Forge page (ForgeHub.vue): no tool tiles; the page's menu picks the tool and only that one shows
        single: { type: Boolean, default: false },
        only: { type: Array, default: null },
        startKind: { type: String, default: '' },
        presetTeam: { type: String, default: '' },
        presetQuarter: { type: String, default: '' },
        // From Meetings.vue: { id, name, team, columns: [names], items, history, plannedMinutes, employees }
        meeting: { type: Object, default: null },
        // From CompanyBoard.vue: { slug, name, columns: [names], cards }
        board: { type: Object, default: null }
    },
    emits: [ 'close', 'applied' ],
    data() {
        const q = quarterOf(new Date());
        return {
            LEVELS, DURATIONS,
            kind: '' as string,
            docked: false, dockTop: 0, // modal on a wide screen: the side panel (placeDock)
            phase: 'input' as string,   // input | loading | review | done
            description: '',
            feedback: '',
            draft: null as any,
            busy: false,
            confirmTexts: false,
            loadError: '',
            applyError: '',
            loadingText: '',
            team: '' as string,
            quarter: q,
            quarterOptions: [ q, nextQuarterOf(q) ],
            matrixID: null as any,
            pickedRocks: [] as number[],
            pickedMetrics: [] as number[],
            inbox: [] as any[],
            inboxSlug: '', // your inbox board (API routes/inbox.ts), which Sort the Inbox moves cards off
            processQ: '',
            // Loaded on demand per action
            scoreboard: null as any,   // { Teams, Metrics, Employees }
            rocksData: {} as any,      // quarter -> { Rocks, Employees, Teams }
            matrices: [] as any[],
            matrixRows: {} as any,     // matrix ID -> rows
            ready: false,
            doneText: '', doneLink: '' as any, doneLinkLabel: '', doneProblems: [] as string[]
        };
    },
    computed: {
        user(): any { return this.$store.state.user || {}; },
        isTeamHubAdmin(): boolean { return this.user.teamHubRole == 'Admin'; },
        isManager(): boolean { return !!(this.user.teamHubEmployee && this.user.teamHubEmployee.IsManager); },
        locations(): string[] { return (this.user.locations || []).filter((l: any) => typeof l == 'string'); },
        actions(): any[] {
            const all = [
                { key: 'scorecard', label: 'Scorecard', sub: 'Weekly numbers', ok: this.isTeamHubAdmin,
                    placeholder: 'e.g. Cincy production: we care about output, scrap, safety and shipping on time. Owners are the shift leads.',
                    hint: 'The Forge suggests weekly numbers with owners and goals.' },
                { key: 'improvemetrics', label: 'Improve Scorecard', sub: 'Sharpen the numbers you have', ok: this.isTeamHubAdmin,
                    placeholder: 'Optional: anything the Forge should know, e.g. our numbers come from NetSuite and the shift reports.',
                    hint: 'Suggests clearer names and definitions, and an owner where one is missing. Goals stay as they are.' },
                { key: 'rocks', label: 'Rocks', sub: 'Quarterly priorities', ok: this.isTeamHubAdmin || !!this.user.teamHubEmployee,
                    placeholder: 'e.g. This quarter we need to fix late shipments, hire two operators and launch the new quote process. Or paste a vague rock to tighten up.',
                    hint: 'Each Rock gets an owner, a clear done statement and dated milestones.' },
                { key: 'milestones', label: 'Milestones', sub: 'For Rocks you have', ok: this.isTeamHubAdmin || !!this.user.teamHubEmployee,
                    placeholder: 'Optional: anything the Forge should know, e.g. the hiring Rock needs interviews done by mid-November.',
                    hint: 'The Forge adds the missing steps to each Rock; its current milestones stay.' },
                { key: 'improve', label: 'Improve Rocks', sub: 'Sharpen the ones you have', ok: this.isTeamHubAdmin || !!this.user.teamHubEmployee,
                    placeholder: 'Optional: anything the Forge should know, e.g. every done statement needs a number and a date.',
                    hint: 'Suggests a clearer name and "done looks like" for each Rock. You choose which to keep.' },
                { key: 'training', label: 'Training', sub: 'Plan a session', ok: this.isTeamHubAdmin || this.isManager,
                    placeholder: 'e.g. Onboarding for two new production hires next Tuesday: safety, the expander line and quality checks.',
                    hint: 'The Forge picks the matching processes from your playbook.' },
                { key: 'skills', label: 'Skills matrix', sub: 'Skills and ratings', ok: this.isTeamHubAdmin,
                    placeholder: 'e.g. Add forklift and shrink wrapping to Cincy Production. Mike is an expert on forklift, Sarah has some competence.',
                    hint: 'Ratings: 0 none · 1 low · 2 some · 3 high · 4 expert.' },
                { key: 'announcement', label: 'Announcement', sub: 'Text the team', ok: this.isTeamHubAdmin && this.locations.length > 0,
                    placeholder: 'e.g. Let Pincher Creek know the plant is closed Monday for Thanksgiving, back Tuesday at 7.',
                    hint: 'You choose the locations before anything is sent.' },
                { key: 'event', label: 'Team event', sub: 'Celebrations', ok: this.isTeamHubAdmin,
                    placeholder: 'e.g. Customer appreciation BBQ at Cincinnati on Friday the 30th at noon, about two hours. Everyone welcome.',
                    hint: 'Shows on Celebrations; you can also send it out as an announcement.' }
            ];
            all.unshift(
                { key: 'tidyboard', label: 'Tidy my board', sub: 'Overdue, stale, undated cards', ok: !!this.board,
                    placeholder: 'Optional: anything the Forge should know, e.g. the Project column is for Q4 only.',
                    hint: 'Suggests cards to mark done, move, or turn into a Rock, and a due date for every card and checklist step without one. You choose which to apply.' },
                { key: 'planweek', label: 'Plan my week', sub: 'What to focus on', ok: !!this.board,
                    placeholder: 'Optional: e.g. I\'m out Thursday and Friday.',
                    hint: 'Picks up to five cards from due dates and your Rocks.' },
                { key: 'sortinbox', label: 'Sort the Inbox', sub: 'Titles, columns, dates', ok: !!this.board,
                    placeholder: 'Optional: e.g. anything about the roof goes in Personal.',
                    hint: 'Gives each Inbox note a clear title, a column and a due date. You check them before they go on the board.' },
                { key: 'prepl10', label: 'Prep for my meeting', sub: 'Issues from Rocks, numbers, to-dos', ok: !!this.meeting,
                    placeholder: 'Optional: anything the Forge should know, e.g. leave out the hiring Rock, we covered it Monday.',
                    hint: 'Drafts Issues from what the facts show. You choose which to add.' },
                { key: 'meetingcheckup', label: 'Improve my meeting', sub: 'What to change, from how it\'s going', ok: !!this.meeting,
                    placeholder: 'Optional: anything you want it to look at, e.g. are we running long?',
                    hint: 'Looks at to-dos, Issues, meeting length, Rocks and numbers, then suggests a change or two.' });
            return all.filter(a => a.ok && (!this.only || this.only.includes(a.key)));
        },
        current(): any { return this.actions.find((a: any) => a.key == this.kind) || {}; },
        // Single mode: the tool family the open kind belongs to, and its versions (only those this person can use)
        family(): string { return Object.keys(FAMILIES).find(f => FAMILIES[f].some(v => v.key == this.kind)) || ''; },
        variants(): any[] { return this.family ? FAMILIES[this.family].filter(v => this.actions.some((a: any) => a.key == v.key)) : []; },
        familyLabel(): string { return this.family == 'scorecard' ? 'Scorecard' : this.family == 'rocks' ? 'Rocks' : (this.current.label || 'The Forge'); },
        usesRocks(): boolean { return [ 'rocks', 'milestones', 'improve' ].includes(this.kind); },
        forMeeting(): boolean { return this.kind == 'prepl10' || this.kind == 'meetingcheckup'; },
        forBoard(): boolean { return [ 'tidyboard', 'planweek', 'sortinbox' ].includes(this.kind); },
        // A board's cards as the Forge sees them (the last column is where finished cards go)
        boardFacts(): any {
            const b: any = this.board || {};
            const cols: string[] = b.columns || [];
            const last = cols[cols.length - 1];
            const t = localDate();
            const sunday = (() => { const d = new Date(); d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); return localDate(d); })();
            const text = (h: any) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
            const cards = (b.cards || []).filter((c: any) => !c.InboxItem).map((c: any) => {
                const done = !!c.Checked || c.Column == last;
                const changed = c.LastModified ? Math.floor((Date.now() - new Date(c.LastModified).getTime()) / 864e5) : null;
                return { c, cardID: Number(c.ID), name: c.Name, column: c.Column, due: c.Date || null, overdue: !done && !!c.Date && c.Date < t, done,
                    daysSinceChange: changed, checklist: c.TotalChecklistItems ? `${c.CheckedChecklistItems}/${c.TotalChecklistItems}` : null,
                    tags: c.TagsJSON || [], description: text(c.Description), fromMeeting: !!c.MeetingItem };
            });
            const open = cards.filter((c: any) => !c.done);
            // Checklist steps with no date on open cards (Tidy my board's due-date pass); ref = cardID.list.item
            const steps: any[] = [];
            for(const c of open) {
                if(c.fromMeeting || steps.length >= 20) continue;
                let lists: any[] = [];
                try { lists = typeof c.c.ChecklistJSON == 'string' ? JSON.parse(c.c.ChecklistJSON || '[]') : (c.c.ChecklistJSON || []); } catch(e) { lists = []; }
                let perCard = 0;
                (Array.isArray(lists) ? lists : []).forEach((list: any, li: number) => ((list && list.items) || []).forEach((it: any, ii: number) => {
                    if(it && !it.checked && !it.date && perCard < 5 && steps.length < 20) {
                        perCard++;
                        steps.push({ ref: `${c.cardID}.${li}.${ii}`, cardID: c.cardID, step: String(it.name || '').slice(0, 160), checklist: list.name || '', cardDue: c.due });
                    }
                }));
            }
            return { cards, open, last, overdue: open.filter((c: any) => c.overdue).length, stale: open.filter((c: any) => (c.daysSinceChange || 0) >= 21).length,
                dueThisWeek: open.filter((c: any) => c.due && c.due <= sunday).length, undated: open.filter((c: any) => !c.due).length, steps };
        },
        myEmployeeID(): any { return this.user.teamHubEmployee ? Number(this.user.teamHubEmployee.ID) : null; },
        todayDate(): string { return localDate(); },
        // Tidy my board's due-date pass suggests dates up to two weeks out
        latestDue(): string { const d = new Date(); d.setDate(d.getDate() + 14); return localDate(d); },
        dueActions(): any[] { return this.kind == 'tidyboard' && this.draft ? (this.draft.actions || []).filter((a: any) => a.action == 'due') : []; },
        myRocks(): any[] {
            const r = this.rocksData[this.quarterOptions[0]];
            return ((r && r.Rocks) || []).filter((x: any) => isRockOwner(x, this.myEmployeeID) && ![ 'done', 'notdone' ].includes(x.Status)).map((x: any) => ({
                name: x.Name, status: x.Status == 'off' ? 'off track' : 'on track', doneStatement: x.DoneStatement || null,
                nextMilestones: (x.Milestones || []).filter((m: any) => !m.Done).slice(0, 3).map((m: any) => ({ text: m.Text, due: m.DueDate })) }));
        },
        // What a meeting's Forge actions work from: the board (meeting prop) plus the team's Rocks and scorecard
        facts(): any {
            const m: any = this.meeting || {};
            const cols: string[] = m.columns || [];
            const issuesColumn = cols.find((c: string) => /issue/i.test(c)) || cols[0] || 'Issues';
            const todoColumn = cols.find((c: string) => /to.?do/i.test(c)) || null;
            const items: any[] = m.items || [];
            const t = localDate();
            const name = (id: any) => (this.employees.find((e: any) => e.ID == id) || {}).Name || null;
            const todos = todoColumn ? items.filter((i: any) => i.Column == todoColumn) : items.filter((i: any) => i.DueDate);
            const rocks = this.meeting && m.team ? (((this.rocksData[this.quarter] || {}).Rocks) || []).filter((r: any) => rockTeamsOf(r).includes(m.team) && ![ 'done', 'notdone' ].includes(r.Status) && !r.CarriedToQuarter) : [];
            const metrics = this.meeting && m.team ? ((this.scoreboard && this.scoreboard.Metrics) || []).filter((x: any) => x.Team == m.team) : [];
            // The team's due week (its due settings, scoreboard.ts dueWeek): usually the week just finished
            const week = dueWeekOf(metrics), weeks = recentWeeks(week);
            return {
                issuesColumn, todoColumn,
                existingIssues: items.filter((i: any) => i.Column == issuesColumn && !i.Completed).map((i: any) => i.Name),
                overdueTodos: todos.filter((i: any) => !i.Completed && i.DueDate && i.DueDate < t)
                    .map((i: any) => ({ name: i.Name, dueDate: i.DueDate, assigneeEmployeeID: i.AssigneeEmployeeID || null, assignee: name(i.AssigneeEmployeeID) })),
                todos, rocks, metrics,
                offTrackRocks: rocks.filter((r: any) => r.Status == 'off' || lateMilestones(r).length).map((r: any) => ({
                    name: r.Name, status: r.Status == 'off' ? 'off track' : 'on track', ownerEmployeeID: r.OwnerEmployeeID, owner: name(r.OwnerEmployeeID), doneStatement: r.DoneStatement || null,
                    lateMilestones: lateMilestones(r).map((ms: any) => ({ text: ms.Text, due: ms.DueDate })) })),
                scorecard: metrics.map((x: any) => ({ x, streak: offStreak(x, weeks), missing: x.Values[week] === undefined || x.Values[week] === null }))
                    .filter((o: any) => o.streak >= 2 || o.missing).map((o: any) => ({
                        name: o.x.Name, ownerEmployeeID: o.x.OwnerEmployeeID || null, owner: name(o.x.OwnerEmployeeID), goal: goalText(o.x, week),
                        weeksOffGoal: o.streak, notEnteredForDueWeek: o.missing, dueWeek: week,
                        last4Weeks: weeks.slice(-4).map((w: string) => ({ week: w, value: o.x.Values[w] ?? null })) }))
            };
        },
        // Meeting check-up numbers (also sent to the AI as context.stats)
        stats(): any {
            if(!this.meeting) return {};
            const f = this.facts, m: any = this.meeting, t = localDate();
            const since = Date.now() - 56 * 864e5;
            const held = (m.history || []).filter((h: any) => h.EndDate && new Date(h.StartDate).getTime() > since);
            const mins = held.map((h: any) => Math.round((new Date(h.EndDate).getTime() - new Date(h.StartDate).getTime()) / 60000)).filter((n: number) => n > 0 && n < 600);
            const dueDone = f.todos.filter((i: any) => i.DueDate && i.DueDate < t);
            const onTime = dueDone.filter((i: any) => i.Completed && i.CompletedDate && localDate(new Date(i.CompletedDate)) <= i.DueDate);
            return {
                meetingsLast8Weeks: held.length,
                averageMinutes: mins.length ? Math.round(mins.reduce((a: number, b: number) => a + b, 0) / mins.length) : null,
                plannedMinutes: m.plannedMinutes || null,
                todos: f.todos.length, todosDone: f.todos.filter((i: any) => i.Completed).length,
                todosDuePast: dueDone.length, todosDoneOnTime: onTime.length, overdueTodos: f.overdueTodos.length,
                openIssues: f.existingIssues.length,
                rocks: f.rocks.length, rocksOffTrack: f.rocks.filter((r: any) => r.Status == 'off').length,
                metrics: f.metrics.length, metricsOff3Weeks: f.metrics.filter((x: any) => offStreak(x, recentWeeks(dueWeekOf(f.metrics))) >= 3).length
            };
        },
        statTiles(): any[] {
            const s: any = this.stats;
            if(!this.meeting) return [];
            const tiles: any[] = [
                { label: 'meetings held, last 8 weeks', value: s.meetingsLast8Weeks },
                { label: s.plannedMinutes ? `average minutes (planned ${s.plannedMinutes})` : 'average minutes', value: s.averageMinutes ?? '–' },
                { label: 'to-dos due so far done on time', value: s.todosDuePast ? `${s.todosDoneOnTime} of ${s.todosDuePast}` : '–' },
                { label: 'overdue to-dos', value: s.overdueTodos },
                { label: 'open Issues', value: s.openIssues }
            ];
            if(this.meeting.team) {
                tiles.push({ label: 'Rocks off track', value: `${s.rocksOffTrack} of ${s.rocks}` });
                tiles.push({ label: 'numbers off 3+ weeks', value: `${s.metricsOff3Weeks} of ${s.metrics}` });
            }
            return tiles;
        },
        // Actions that work on rocks already there: pick which ones first
        picksRocks(): boolean { return this.kind == 'milestones' || this.kind == 'improve'; },
        canDraft(): boolean {
            if(this.forMeeting) return this.ready && !this.busy;
            if(this.forBoard) return this.ready && !this.busy && (this.kind == 'sortinbox' ? this.inbox.length > 0 : this.boardFacts.open.length > 0);
            return this.ready && !this.busy && (this.picksRocks ? this.pickedRocks.length > 0 : this.kind == 'improvemetrics' ? this.pickedMetrics.length > 0 : !!this.description.trim());
        },
        usesScoreboard(): boolean { return this.kind == 'scorecard' || this.kind == 'improvemetrics'; },
        // The team's metrics (Improve Scorecard)
        teamMetrics(): any[] {
            return ((this.scoreboard && this.scoreboard.Metrics) || []).filter((m: any) => m.Team == this.team);
        },
        // Open rocks on this team that this person can edit (the rocks route's CanEdit)
        editableRocks(): any[] {
            const r = this.rocksData[this.quarter];
            return ((r && r.Rocks) || []).filter((x: any) => rockTeamsOf(x).includes(this.team) && x.CanEdit && ![ 'done', 'notdone' ].includes(x.Status) && !x.CarriedToQuarter);
        },
        // New milestones can't be due before today
        earliest(): string {
            const t = localDate();
            return t < this.bounds.start ? this.bounds.start : t > this.bounds.end ? this.bounds.end : t;
        },
        teamOptions(): string[] {
            if(this.usesScoreboard) return ((this.scoreboard && this.scoreboard.Teams) || []).map((t: any) => t.Name);
            const r = this.rocksData[this.quarter];
            return (r && r.Teams) || [];
        },
        employees(): any[] {
            if(this.forMeeting) {
                // The meeting's people, plus anyone the team's Rocks and scorecard know (owners)
                const all = [ ...((this.meeting && this.meeting.employees) || []), ...(((this.rocksData[this.quarter] || {}).Employees) || []), ...((this.scoreboard && this.scoreboard.Employees) || []) ];
                const seen = new Set();
                return all.filter((e: any) => e && !seen.has(Number(e.ID)) && seen.add(Number(e.ID))).map((e: any) => ({ ...e, ID: Number(e.ID) }));
            }
            if(this.usesScoreboard) return (this.scoreboard && this.scoreboard.Employees) || [];
            const r = this.rocksData[this.quarter];
            return (r && r.Employees) || [];
        },
        bounds(): { start: string, end: string } { return quarterBounds(this.quarter); },
        nodes(): any[] {
            return (this.$store.state.allNodes || []).filter((n: any) => n.Type && n.Type != 'NODE' && n.Name);
        },
        processMatches(): any[] {
            const q = this.processQ.trim().toLowerCase();
            const chosen = (this.draft && this.draft.processSlugs) || [];
            return this.nodes.filter((n: any) => !chosen.includes(n.Slug) && n.Name.toLowerCase().includes(q)).slice(0, 8);
        },
        skillsTarget(): any {
            return this.draft && this.draft.matrixID ? this.matrices.find((m: any) => m.ID == this.draft.matrixID) : null;
        },
        sendLocations(): string[] {
            if(!this.draft) return [];
            if(this.kind == 'announcement') return this.draft.locations;
            if(this.kind == 'event' && this.draft.sendOut) return this.draft.sendLocations;
            return [];
        },
        texting(): boolean { return this.sendLocations.length > 0; },
        keptCount(): number {
            if(!this.draft) return 0;
            if(this.kind == 'scorecard') return this.draft.metrics.filter((m: any) => m.keep).length;
            if(this.kind == 'rocks') return this.draft.rocks.filter((r: any) => r.keep).length;
            if(this.kind == 'milestones') return this.draft.rocks.filter((r: any) => r.keep).reduce((n: number, r: any) => n + r.milestones.length, 0);
            if(this.kind == 'improve') return this.draft.rocks.filter((r: any) => r.keep && this.rockChanged(r)).length;
            if(this.kind == 'improvemetrics') return this.draft.metrics.filter((m: any) => m.keep && this.metricChanged(m)).length;
            if(this.kind == 'prepl10') return this.draft.issues.filter((i: any) => i.keep).length;
            if(this.kind == 'tidyboard') return this.draft.actions.filter((a: any) => a.keep).length;
            if(this.kind == 'planweek') return this.draft.toColumn ? this.draft.picks.filter((x: any) => x.keep && x.column != this.draft.toColumn).length : 0;
            if(this.kind == 'sortinbox') return this.draft.items.filter((x: any) => x.keep).length;
            if(this.kind == 'meetingcheckup') return this.draft.suggestions.filter((x: any) => x.keep).length;
            return 0;
        },
        canApply(): boolean {
            const d = this.draft;
            if(!d) return false;
            switch(this.kind) {
                case 'scorecard': return this.keptCount > 0 && d.metrics.every((m: any) => !m.keep || (m.name.trim() && Number.isFinite(m.goal) && (m.cmp != 'range' || Number.isFinite(m.goalMax))));
                case 'rocks': return this.keptCount > 0 && d.rocks.every((r: any) => !r.keep || (r.name.trim() && r.milestones.every((ms: any) => ms.text.trim() && validDate(ms.dueDate))));
                case 'milestones': return this.keptCount > 0 && d.rocks.every((r: any) => !r.keep || r.milestones.every((ms: any) => ms.text.trim() && validDate(ms.dueDate)));
                case 'improve': return this.keptCount > 0 && d.rocks.every((r: any) => !r.keep || !!r.name.trim());
                case 'improvemetrics': return this.keptCount > 0 && d.metrics.every((m: any) => !m.keep || !!m.name.trim());
                case 'prepl10': return this.keptCount > 0 && d.issues.every((i: any) => !i.keep || !!i.name.trim());
                case 'meetingcheckup': return this.keptCount > 0;
                case 'tidyboard': return this.keptCount > 0 && d.actions.every((a: any) => !a.keep || (a.action == 'rock' ? !!(a.rockName || '').trim() : a.action == 'due' ? validDate(a.dueDate) : true));
                case 'planweek': return this.keptCount > 0;
                case 'sortinbox': return this.keptCount > 0 && d.items.every((x: any) => !x.keep || (x.name.trim() && this.board.columns.includes(x.column)));
                case 'training': return !!(d.name.trim() && validDate(d.date) && (d.type == 'On-site' || d.processSlugs.length));
                case 'skills': return !!((d.matrixID || (d.newMatrixName || '').trim()) && (d.addSkills.length || d.addPeople.length || d.ratings.some((r: any) => r.keep)));
                case 'announcement': return !!(d.text.trim() && d.locations.length);
                case 'event': return !!(d.name.trim() && validDate(d.date) && (!d.sendOut || (d.announcementText.trim() && d.sendLocations.length)));
            }
            return false;
        },
        applyLabel(): string {
            const d = this.draft;
            if(!d) return '';
            if([ 'improve', 'improvemetrics' ].includes(this.kind) && !this.keptCount) return 'Nothing to change';
            if(this.forMeeting && !this.keptCount) return this.kind == 'prepl10' ? 'Nothing to add' : 'Tick a suggestion to add it';
            if(this.kind == 'planweek' && !this.keptCount) return d.toColumn ? 'Nothing to move' : 'Pick a column to move them to';
            if(this.forBoard && !this.keptCount) return 'Nothing to apply';
            if([ 'scorecard', 'rocks', 'milestones' ].includes(this.kind) && !this.keptCount) return 'Nothing to add';
            switch(this.kind) {
                case 'scorecard': return `Add ${this.keptCount} metric${this.keptCount == 1 ? '' : 's'} to ${this.teamLabel(this.team)}`;
                case 'rocks': return `Add ${this.keptCount} Rock${this.keptCount == 1 ? '' : 's'}`;
                case 'milestones': {
                    const rocks = d.rocks.filter((r: any) => r.keep && r.milestones.length).length;
                    return `Add ${this.keptCount} milestone${this.keptCount == 1 ? '' : 's'} to ${rocks} Rock${rocks == 1 ? '' : 's'}`;
                }
                case 'improve': return `Update ${this.keptCount} Rock${this.keptCount == 1 ? '' : 's'}`;
                case 'improvemetrics': return `Update ${this.keptCount} metric${this.keptCount == 1 ? '' : 's'}`;
                case 'prepl10': return `Add ${this.keptCount} Issue${this.keptCount == 1 ? '' : 's'}`;
                case 'tidyboard': return `Apply ${this.keptCount} change${this.keptCount == 1 ? '' : 's'}`;
                case 'planweek': return `Move ${this.keptCount} card${this.keptCount == 1 ? '' : 's'} to ${d.toColumn}`;
                case 'sortinbox': return `Add ${this.keptCount} to the board`;
                case 'meetingcheckup': return `Add ${this.keptCount} to ${this.facts.issuesColumn}`;
                case 'training': return d.type == 'On-site' ? 'Create on-site session' : 'Create classroom';
                case 'skills': return this.skillsTarget ? `Update ${this.skillsTarget.Name}` : 'Create matrix';
                case 'announcement': return `Post and text ${d.locations.length} location${d.locations.length == 1 ? '' : 's'}`;
                case 'event': return d.sendOut ? `Create event and text ${d.sendLocations.length} location${d.sendLocations.length == 1 ? '' : 's'}` : 'Create event';
            }
            return 'Apply';
        }
    },
    mounted() {
        if(this.modal) { this.placeDock(); window.addEventListener('resize', this.placeDock); }
        if(this.startKind && this.actions.some((a: any) => a.key == this.startKind)) {
            if(this.presetTeam) this.team = this.presetTeam;
            if(this.quarterOptions.includes(this.presetQuarter)) this.quarter = this.presetQuarter;
            this.pick(this.startKind);
        }
    },
    watch: {
        async quarter() {
            if(this.usesRocks) await this.loadRocks();
            this.pickAllRocks();
        },
        team() { this.pickAllRocks(); this.pickAllMetrics(); },
        draft: { deep: true, handler() { this.confirmTexts = false; } }
    },
    beforeUnmount() {
        if(this.modal) { window.removeEventListener('resize', this.placeDock); this.setDocked(false); }
    },
    methods: {
        // From DOCK_MIN_WIDTH wide, a modal Forge tool docks on the right under the page's bar (see ai-theme.css for the
        // room the page makes); the app folds the left menu while it is open (store.aiDocked, App.vue)
        placeDock() {
            const bar = document.querySelector('#app-main .page-bar-sticky, #app-main .page-bar, #app-main .meeting-page .page-bar') as HTMLElement | null;
            const dock = window.innerWidth >= FT_DOCK_MIN_WIDTH;
            if (dock) this.dockTop = Math.max(0, Math.round(bar ? bar.getBoundingClientRect().bottom : 0));
            this.setDocked(dock);
        },
        setDocked(on: boolean) {
            this.docked = on;
            document.body.classList.toggle('ft-docked-on', on);
            if (this.$store.state.aiDocked !== on) this.$store.state.aiDocked = on;
        },
        teamLabel(t: string): string { return t == COMPANY_TEAM ? 'Company' : t; },
        rockChanged(r: any): boolean { return r.name.trim() != r.oldName || r.doneStatement.trim() != r.oldDone; },
        metricChanged(m: any): boolean {
            return m.name.trim() != m.old.name || m.definition.trim() != m.old.definition || m.unit.trim() != m.old.unit || (m.ownerEmployeeID || null) != (m.old.ownerEmployeeID || null);
        },
        actionLabel(a: any): string { return a.action == 'done' ? 'Mark done' : a.action == 'rock' ? 'Turn into a Rock' : a.action == 'due' ? (a.checklistRef ? 'Date this step' : 'Set a due date') : 'Move to'; },
        sourceLabel(src: string): string { return src == 'rock' ? 'Rock' : src == 'scorecard' ? 'Scorecard' : src == 'todo' ? 'To-dos' : 'Other'; },
        personName(id: any): string { const e = this.employees.find((x: any) => x.ID == id); return e ? e.Name : 'someone not on this team'; },
        pickAllMetrics() { this.pickedMetrics = this.kind == 'improvemetrics' ? this.teamMetrics.map((m: any) => m.ID) : []; },
        quarterLabel(q: string): string { return `Q${q.slice(6)} ${q.slice(0, 4)}`; },
        shortDate(d: string): string { return new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); },
        durationLabel(m: number): string { return m < 60 ? `${m} min` : `${m / 60} hour${m == 60 ? '' : 's'}`; },
        nodeName(slug: string): string { const n = this.nodes.find((x: any) => x.Slug == slug); return n ? n.Name : slug; },
        errorText(e: any, fallback: string): string {
            const d = e?.response?.data;
            if(e?.response?.status == 403) return 'You don\'t have permission for that here.';
            if(e?.response?.status == 429) return typeof d == 'string' ? d : 'Daily AI limit reached.';
            return typeof d == 'string' && d && d.length < 300 ? d : fallback;
        },
        async pick(key: string) {
            if(this.busy) return;
            this.kind = key;
            this.startOver(false);
            this.ready = false;
            this.loadError = '';
            try {
                if((key == 'scorecard' || key == 'improvemetrics') && !this.scoreboard) {
                    this.scoreboard = (await axios.get(`${this.$store.state.apiUrl}/api/scoreboard`, { params: { week: mondayOf(new Date()) } })).data;
                }
                if(this.usesRocks) await this.loadRocks();
                if(key == 'skills') this.matrices = (await axios.get(`${this.$store.state.apiUrl}/api/skillsmatrices`)).data || [];
                if(key == 'training') {
                    for(let i = 0; i < 100 && !this.$store.state.allNodesLoaded; i++) await new Promise(r => setTimeout(r, 200));
                }
                if(this.forBoard) {
                    // Rocks you own (Plan my week; and whether Tidy can suggest turning a card into one)
                    const q = this.quarterOptions[0];
                    if(!this.rocksData[q]) this.rocksData[q] = (await axios.get(`${this.$store.state.apiUrl}/api/rocks`, { params: { quarter: q } }).catch(() => ({ data: {} }))).data;
                    if(key == 'sortinbox') await this.loadInbox();
                } else if(this.forMeeting) {
                    // A team meeting also looks at the team's Rocks (this quarter) and scorecard
                    if(this.meeting.team) {
                        this.quarter = this.quarterOptions[0];
                        this.team = this.meeting.team;
                        await this.loadRocks().catch(() => null);
                        if(!this.scoreboard) this.scoreboard = (await axios.get(`${this.$store.state.apiUrl}/api/scoreboard`, { params: { week: mondayOf(new Date()) } }).catch(() => ({ data: null }))).data;
                    }
                } else if(this.usesScoreboard || this.usesRocks) {
                    const opts = this.teamOptions;
                    const mine = this.user.teamHubEmployee && this.user.teamHubEmployee.Team;
                    if(!opts.includes(this.team)) this.team = opts.find(t => t == mine) || opts.find(t => t != COMPANY_TEAM) || opts[0] || '';
                    if(!opts.length) this.loadError = 'You aren\'t on any team yet, so there\'s nowhere to add these.';
                }
                this.pickAllRocks();
                this.pickAllMetrics();
                this.ready = true;
            } catch(e: any) {
                this.loadError = this.errorText(e, 'Couldn\'t load what this needs. Please try again.');
            }
        },
        pickAllRocks() {
            this.pickedRocks = this.picksRocks ? this.editableRocks.map((r: any) => r.ID) : [];
        },
        // Your inbox's cards, in short (ID, Name, Description, CreateDate), and its board
        async loadInbox() {
            const data = (await axios.get(`${this.$store.state.apiUrl}/api/inbox`)).data || {};
            this.inbox = data.Items || [];
            this.inboxSlug = data.Slug || '';
        },
        async loadRocks() {
            if(!this.rocksData[this.quarter]) {
                this.rocksData[this.quarter] = (await axios.get(`${this.$store.state.apiUrl}/api/rocks`, { params: { quarter: this.quarter } })).data;
            }
        },
        async matrixWithRows(id: number): Promise<any> {
            const m = (await axios.get(`${this.$store.state.apiUrl}/api/skillsmatrices/${id}`)).data;
            const rows = (await axios.get(`${this.$store.state.apiUrl}/api/skillsmatrices/rows/${id}`)).data || [];
            this.matrixRows[id] = rows;
            return { ...m, rows };
        },
        parseJSON(v: any, fallback: any) {
            if(v && typeof v == 'object') return v;
            try { return JSON.parse(v || ''); } catch(e) { return fallback; }
        },
        // What the AI sees: only what this person's own pages already show them
        async contextFor(): Promise<any> {
            const today = localDate();
            const people = (list: any[]) => list.map((e: any) => ({ ID: e.ID, Name: e.Name, Teams: e.Teams || (e.Team ? [ e.Team ] : []) }));
            switch(this.kind) {
                case 'scorecard': return {
                    team: this.teamLabel(this.team), today, employees: people(this.employees),
                    existingMetrics: (this.scoreboard.Metrics || []).filter((m: any) => m.Team == this.team).map((m: any) => ({ Name: m.Name, Unit: m.Unit, Definition: m.Definition }))
                };
                case 'tidyboard': return { today, latestDueDate: this.latestDue, board: { name: this.board.name, columns: this.board.columns },
                    cards: this.boardFacts.cards.filter((c: any) => !c.done).map((c: any) => ({ cardID: c.cardID, name: c.name, column: c.column, due: c.due, overdue: c.overdue, daysSinceChange: c.daysSinceChange, checklist: c.checklist, tags: c.tags, description: c.description, fromMeeting: c.fromMeeting })),
                    undatedSteps: this.boardFacts.steps.map((x: any) => ({ ref: x.ref, cardID: x.cardID, step: x.step, checklist: x.checklist })),
                    canMakeRocks: !!this.myEmployeeID && !!(this.user.teamHubEmployee && this.user.teamHubEmployee.Team), myRocks: this.myRocks.map((r: any) => r.name) };
                case 'planweek': return { today, board: { name: this.board.name, columns: this.board.columns },
                    cards: this.boardFacts.open.map((c: any) => ({ cardID: c.cardID, name: c.name, column: c.column, due: c.due, overdue: c.overdue, checklist: c.checklist, tags: c.tags, description: c.description })),
                    myRocks: this.myRocks };
                case 'sortinbox': return { today, board: { name: this.board.name, columns: this.board.columns },
                    inbox: this.inbox.map((x: any) => ({ inboxID: Number(x.ID), name: x.Name, description: htmlToText(x.Description).slice(0, 1000), added: x.CreateDate })) };
                case 'prepl10': {
                    const f = this.facts;
                    return { meeting: this.meeting.name, team: this.meeting.team ? this.teamLabel(this.meeting.team) : null, today, employees: people(this.employees),
                        existingIssues: f.existingIssues, offTrackRocks: f.offTrackRocks, scorecard: f.scorecard, overdueTodos: f.overdueTodos };
                }
                case 'meetingcheckup': return { meeting: this.meeting.name, team: this.meeting.team ? this.teamLabel(this.meeting.team) : null, today, stats: this.stats,
                    overdueTodos: this.facts.overdueTodos.slice(0, 20), offTrackRocks: this.facts.offTrackRocks.map((r: any) => r.name), numbersOffGoal: this.facts.scorecard.map((x: any) => x.name) };
                case 'improvemetrics': {
                    const week = mondayOf(new Date());
                    return {
                        team: this.teamLabel(this.team), today, employees: people(this.employees),
                        metrics: this.teamMetrics.filter((m: any) => this.pickedMetrics.includes(m.ID)).map((m: any) => ({
                            metricID: m.ID, name: m.Name, unit: m.Unit || '', definition: m.Definition || null, ownerEmployeeID: m.OwnerEmployeeID || null, goal: goalText(m, week)
                        }))
                    };
                }
                case 'rocks': return {
                    team: this.teamLabel(this.team), quarter: this.quarter, quarterStart: this.bounds.start, quarterEnd: this.bounds.end, today,
                    employees: people(this.employees),
                    existingRocks: ((this.rocksData[this.quarter] || {}).Rocks || []).filter((r: any) => rockTeamsOf(r).includes(this.team)).map((r: any) => ({ Name: r.Name, DoneStatement: r.DoneStatement }))
                };
                case 'milestones': return {
                    team: this.teamLabel(this.team), quarter: this.quarter, quarterStart: this.bounds.start, quarterEnd: this.bounds.end, today, earliestDate: this.earliest,
                    employees: people(this.employees),
                    rocks: this.editableRocks.filter((r: any) => this.pickedRocks.includes(r.ID)).map((r: any) => ({
                        rockID: r.ID, name: r.Name, doneStatement: r.DoneStatement || null, ownerEmployeeID: r.OwnerEmployeeID, teamRock: !!r.TeamRock,
                        existingMilestones: r.Milestones.map((m: any) => ({ text: m.Text, dueDate: m.DueDate, done: !!m.Done, ownerEmployeeID: m.OwnerEmployeeID }))
                    }))
                };
                case 'improve': return {
                    team: this.teamLabel(this.team), quarter: this.quarter, quarterStart: this.bounds.start, quarterEnd: this.bounds.end, today,
                    rocks: this.editableRocks.filter((r: any) => this.pickedRocks.includes(r.ID)).map((r: any) => ({
                        rockID: r.ID, name: r.Name, doneStatement: r.DoneStatement || null, teamRock: !!r.TeamRock, milestones: r.Milestones.map((m: any) => m.Text)
                    }))
                };
                case 'training': return { today, locations: this.locations, processes: this.nodes.map((n: any) => ({ slug: n.Slug, name: n.Name, type: n.Type })) };
                case 'skills': {
                    const matrices = this.matrices.map((m: any) => ({ ID: m.ID, Name: m.Name, skills: this.parseJSON(m.ColumnsJSON, []).map((c: any) => c.name) }));
                    let selected = null;
                    if(this.matrixID) {
                        const m = await this.matrixWithRows(this.matrixID);
                        const cols = this.parseJSON(m.ColumnsJSON, []);
                        selected = { ID: m.ID, Name: m.Name, people: m.rows.map((r: any) => {
                            const levels = this.parseJSON(r.ColumnsJSON, {});
                            return { name: r.Name, ratings: Object.fromEntries(cols.map((c: any) => [ c.name, levels[c.id] || 0 ])) };
                        }) };
                    }
                    return { today, matrices, selectedMatrix: selected };
                }
                case 'announcement': return { today, locations: this.locations };
                case 'event': return { today, timeZone: browserTimeZone(), locations: this.locations };
            }
            return {};
        },
        async draftIt(revise = false) {
            if(!this.canDraft) return;
            const previous = revise ? this.cleanForAI(this.draft) : null;
            this.busy = true;
            this.applyError = '';
            this.loadingText = revise ? 'Revising the draft…' : 'Drafting…';
            const back = this.phase;
            this.phase = 'loading';
            try {
                const context = await this.contextFor();
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/team-draft`, {
                    kind: this.kind, description: this.description.trim() || (this.kind == 'improve' ? 'Make each of these Rocks clear and measurable.' : this.kind == 'improvemetrics' ? 'Make each of these metrics clear, with a definition and an owner.' : this.kind == 'prepl10' ? 'Draft the Issues for this week\'s meeting.' : this.kind == 'tidyboard' ? 'Tidy this board.' : this.kind == 'planweek' ? 'What should I focus on this week?' : this.kind == 'sortinbox' ? 'Sort my Inbox onto this board.' : this.kind == 'meetingcheckup' ? 'How is this meeting going, and what should we change?' : 'Plan the milestones for each of these Rocks.'), context, previous, feedback: revise ? this.feedback : null
                });
                this.draft = this.checkDraft(resp.data);
                this.feedback = '';
                this.phase = 'review';
            } catch(e: any) {
                this.phase = back == 'loading' ? 'input' : back;
                (this.phase == 'review' ? (msg: string) => { this.applyError = msg; } : (msg: string) => { this.loadError = msg; })(this.errorText(e, 'The Forge couldn\'t draft that. Please try again.'));
            }
            this.busy = false;
        },
        // Keeps only what the page can actually use: known people, processes, locations, matrices and sane values
        checkDraft(d: any): any {
            const ids = new Set(this.employees.map((e: any) => e.ID));
            const person = (id: any) => ids.has(id) ? id : null;
            const locs = (list: any) => (Array.isArray(list) ? list : []).filter((l: any) => this.locations.includes(l));
            switch(this.kind) {
                case 'scorecard':
                    d.metrics = (d.metrics || []).map((m: any) => ({ ...m, keep: true, ownerEmployeeID: person(m.ownerEmployeeID), unit: String(m.unit || '').slice(0, 12),
                        goalMax: m.cmp == 'range' ? m.goalMax : null }));
                    break;
                case 'rocks': {
                    const inQ = (s: any) => validDate(s) && s >= this.bounds.start && s <= this.bounds.end ? s : this.bounds.end;
                    d.rocks = (d.rocks || []).map((r: any) => ({ ...r, keep: true, ownerEmployeeID: person(r.ownerEmployeeID),
                        milestones: (r.milestones || []).map((ms: any) => ({ text: ms.text, ownerEmployeeID: person(ms.ownerEmployeeID), dueDate: inQ(ms.dueDate) })) }));
                    break;
                }
                case 'milestones': {
                    // One entry per picked rock, in the page's order; dates between today and the quarter's end
                    const inQ = (s: any) => validDate(s) && s >= this.earliest && s <= this.bounds.end ? s : this.bounds.end;
                    const byID = new Map((d.rocks || []).map((r: any) => [ r.rockID, r ]));
                    d.rocks = this.editableRocks.filter((r: any) => this.pickedRocks.includes(r.ID)).map((rock: any) => {
                        const r: any = byID.get(rock.ID) || {};
                        return { rockID: rock.ID, name: rock.Name, doneStatement: rock.DoneStatement, ownerEmployeeID: rock.OwnerEmployeeID, existing: rock.Milestones, keep: true,
                            milestones: (r.milestones || []).filter((ms: any) => String(ms.text || '').trim())
                                .map((ms: any) => ({ text: String(ms.text).trim(), ownerEmployeeID: person(ms.ownerEmployeeID), dueDate: inQ(ms.dueDate) })) };
                    });
                    break;
                }
                case 'improve': {
                    // One entry per picked rock, in the page's order; a rock the AI skipped keeps its wording (no change)
                    const byID = new Map((d.rocks || []).map((r: any) => [ r.rockID, r ]));
                    d.rocks = this.editableRocks.filter((rock: any) => this.pickedRocks.includes(rock.ID)).map((rock: any) => {
                        const r: any = byID.get(rock.ID) || {};
                        const oldName = String(rock.Name || '').trim(), oldDone = String(rock.DoneStatement || '').trim();
                        const row = { rockID: rock.ID, oldName, oldDone, why: String(r.why || ''),
                            name: (String(r.name || '').trim() || oldName).slice(0, 200), doneStatement: (String(r.doneStatement || '').trim() || oldDone).slice(0, 1000), keep: true };
                        row.keep = this.rockChanged(row);
                        return row;
                    });
                    break;
                }
                case 'tidyboard': {
                    const byID = new Map(this.boardFacts.cards.map((c: any) => [ c.cardID, c ]));
                    const steps = new Map(this.boardFacts.steps.map((x: any) => [ x.ref, x ]));
                    const rocksOK = !!this.myEmployeeID && !!(this.user.teamHubEmployee && this.user.teamHubEmployee.Team);
                    // One move/done/rock per card, plus one date per undated card or checklist step
                    const seen = new Set();
                    const key = (a: any) => a.action == 'due' ? 'due:' + (a.checklistRef || a.cardID) : 'act:' + a.cardID;
                    const inWindow = (s: any) => validDate(s) && s >= this.todayDate && s <= this.latestDue;
                    const valid = (a: any, c: any): boolean => {
                        if(a.action == 'move') return this.board.columns.includes(a.toColumn) && a.toColumn != c.column;
                        if(a.action == 'rock') return rocksOK && !!String(a.rockName || '').trim();
                        if(a.action == 'due') {
                            if(!inWindow(a.dueDate)) return false;
                            if(!a.checklistRef) return !c.due;
                            const st: any = steps.get(a.checklistRef);
                            return !!st && st.cardID == c.cardID && (!c.due || a.dueDate <= c.due);
                        }
                        return a.action == 'done';
                    };
                    d.actions = (d.actions || []).filter((a: any) => {
                        const c: any = byID.get(a.cardID);
                        if(!c || c.done || seen.has(key(a)) || !valid(a, c)) return false;
                        seen.add(key(a));
                        return true;
                    }).map((a: any) => { const c: any = byID.get(a.cardID); const st: any = a.action == 'due' && a.checklistRef ? steps.get(a.checklistRef) : null;
                        return { ...a, card: c.c, cardName: c.name, reason: String(a.reason || ''), rockName: String(a.rockName || ''), doneStatement: String(a.doneStatement || ''),
                            dueDate: a.action == 'due' ? a.dueDate : null, checklistRef: st ? st.ref : null, stepName: st ? st.step : '', maxDate: st && c.due ? c.due : null, keep: true }; })
                        // The tidy-up actions first, then dates: cards before their steps
                        .sort((x: any, y: any) => (x.action == 'due' ? 1 : 0) - (y.action == 'due' ? 1 : 0) || (x.checklistRef ? 1 : 0) - (y.checklistRef ? 1 : 0));
                    break;
                }
                case 'planweek': {
                    const byID = new Map(this.boardFacts.open.map((c: any) => [ c.cardID, c ]));
                    const seen = new Set();
                    d.picks = (d.picks || []).filter((x: any) => byID.has(x.cardID) && !seen.has(x.cardID) && seen.add(x.cardID)).slice(0, 5).map((x: any) => {
                        const c: any = byID.get(x.cardID); return { cardID: c.cardID, card: c.c, name: c.name, column: c.column, due: c.due, why: String(x.why || ''), keep: true }; });
                    d.toColumn = this.board.columns.find((c: string) => /this week|week|today|up next|doing|focus/i.test(c)) || '';
                    break;
                }
                case 'sortinbox': {
                    const cols = this.board.columns, firstOpen = cols[0];
                    const byID = new Map((d.items || []).map((x: any) => [ x.inboxID, x ]));
                    d.items = this.inbox.map((raw: any) => {
                        const x: any = byID.get(Number(raw.ID)) || {};
                        return { inboxID: Number(raw.ID), raw, original: [ raw.Name, htmlToText(raw.Description) ].filter(Boolean).join(' · ').slice(0, 200),
                            name: (String(x.name || '').trim() || raw.Name || '').slice(0, 255), column: cols.includes(x.column) && x.column != cols[cols.length - 1] ? x.column : firstOpen,
                            dueDate: validDate(x.dueDate) ? x.dueDate : '', keep: true };
                    });
                    break;
                }
                case 'prepl10':
                    d.issues = (d.issues || []).filter((i: any) => String(i.name || '').trim()).map((i: any) => ({ name: String(i.name).trim().slice(0, 200), description: String(i.description || ''),
                        source: [ 'rock', 'scorecard', 'todo' ].includes(i.source) ? i.source : 'other', priority: [ 'high', 'med', 'low' ].includes(i.priority) ? i.priority : 'med',
                        assigneeEmployeeID: person(i.assigneeEmployeeID), keep: true }));
                    break;
                case 'meetingcheckup':
                    d.suggestions = (d.suggestions || []).filter((x: any) => String(x.title || '').trim()).slice(0, 3).map((x: any) => ({ title: String(x.title).trim(), detail: String(x.detail || ''), keep: false }));
                    break;
                case 'improvemetrics': {
                    // One entry per picked metric, in the page's order; a metric the AI skipped stays as it is
                    const byID = new Map((d.metrics || []).map((m: any) => [ m.metricID, m ]));
                    const week = mondayOf(new Date());
                    d.metrics = this.teamMetrics.filter((metric: any) => this.pickedMetrics.includes(metric.ID)).map((metric: any) => {
                        const m: any = byID.get(metric.ID) || {};
                        const old = { name: String(metric.Name || '').trim(), definition: String(metric.Definition || '').trim(), unit: String(metric.Unit || '').trim(), ownerEmployeeID: metric.OwnerEmployeeID || null };
                        // An owner only where there wasn't one, and only someone the page knows
                        const owner = old.ownerEmployeeID || person(m.ownerEmployeeID);
                        const row = { metricID: metric.ID, old, goalText: goalText(metric, week), why: String(m.why || ''),
                            name: (String(m.name || '').trim() || old.name).slice(0, 80), definition: (String(m.definition || '').trim() || old.definition).slice(0, 1000),
                            unit: (m.unit == null ? old.unit : String(m.unit).trim()).slice(0, 12), ownerEmployeeID: owner, keep: true };
                        row.keep = this.metricChanged(row);
                        return row;
                    });
                    break;
                }
                case 'training': {
                    const slugs = new Set(this.nodes.map((n: any) => n.Slug));
                    d.processSlugs = [ ...new Set((d.processSlugs || []).filter((s: any) => slugs.has(s))) ];
                    d.date = validDate(d.date) ? d.date : '';
                    d.type = d.type == 'On-site' ? 'On-site' : 'Classroom';
                    break;
                }
                case 'skills':
                    if(!this.matrices.some((m: any) => m.ID == d.matrixID)) d.matrixID = this.matrixID || null;
                    if(!d.matrixID && !d.newMatrixName) d.newMatrixName = 'New skills matrix';
                    d.addSkills = [ ...new Set((d.addSkills || []).map((s: any) => String(s).trim()).filter(Boolean)) ];
                    d.addPeople = [ ...new Set((d.addPeople || []).map((s: any) => String(s).trim()).filter(Boolean)) ];
                    d.ratings = (d.ratings || []).filter((r: any) => r.person && r.skill && r.level >= 0 && r.level <= 4).map((r: any) => ({ ...r, keep: true }));
                    break;
                case 'announcement':
                    d.text = String(d.text || '');
                    d.locations = locs(d.locations);
                    break;
                case 'event':
                    d.date = validDate(d.date) ? d.date : '';
                    d.startTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(d.startTime || '') ? d.startTime : '';
                    d.durationMinutes = Number.isInteger(d.durationMinutes) && d.durationMinutes > 0 ? d.durationMinutes : null;
                    if(d.durationMinutes && !DURATIONS.includes(d.durationMinutes)) DURATIONS.push(d.durationMinutes);
                    d.location = this.locations.includes(d.location) ? d.location : null;
                    d.sendOut = false;
                    d.sendLocations = d.location ? [ d.location ] : this.locations.slice();
                    d.announcementText = String(d.announcementText || '');
                    break;
            }
            return d;
        },
        cleanForAI(d: any): any {
            return JSON.parse(JSON.stringify(d || null, (k, v) => [ 'keep', 'sendOut', 'sendLocations', 'existing', 'oldName', 'oldDone', 'old', 'goalText', 'card', 'raw' ].includes(k) ? undefined : v));
        },
        startOver(clearText = true) {
            this.phase = 'input';
            this.draft = null;
            this.feedback = '';
            this.applyError = '';
            this.confirmTexts = false;
            this.processQ = '';
            if(clearText) this.description = '';
        },
        done(text: string, link: any, label: string, problems: string[] = []) {
            this.doneText = text; this.doneLink = link; this.doneLinkLabel = label; this.doneProblems = problems;
            this.phase = 'done';
            this.description = '';
            this.$emit('applied');
        },
        async apply() {
            if(this.busy || !this.canApply) return;
            // Texts go out immediately: a second click confirms
            if(this.texting && !this.confirmTexts) {
                this.confirmTexts = true;
                return;
            }
            this.busy = true;
            this.applyError = '';
            const api = this.$store.state.apiUrl;
            const d = this.draft;
            const problems: string[] = [];
            try {
                if(this.kind == 'scorecard') {
                    const week = mondayOf(new Date());
                    let added = 0;
                    for(const m of d.metrics.filter((x: any) => x.keep)) {
                        await axios.post(`${api}/api/scoreboard/metric`, { ID: null, Team: this.team, Name: m.name.trim(), OwnerEmployeeID: m.ownerEmployeeID, Unit: m.unit,
                            Definition: m.definition, Cmp: m.cmp, Goal: m.goal, GoalMax: m.cmp == 'range' ? m.goalMax : null, Week: week })
                            .then(() => { added++; }).catch((e: any) => problems.push(`"${m.name}": ${this.errorText(e, 'not added')}.`));
                    }
                    this.scoreboard = null;
                    this.done(`Added ${added} metric${added == 1 ? '' : 's'} to the ${this.teamLabel(this.team)} scorecard.`,
                        { path: '/scoreboard', query: { team: this.team } }, 'Open the scorecard', problems);
                } else if(this.kind == 'rocks') {
                    let added = 0;
                    for(const r of d.rocks.filter((x: any) => x.keep)) {
                        await axios.post(`${api}/api/rocks/rock`, { ID: null, Quarter: this.quarter, Team: this.team, Name: r.name.trim(), OwnerEmployeeID: r.ownerEmployeeID,
                            Status: 'on', DoneStatement: r.doneStatement, TeamRock: !!r.teamRock,
                            Milestones: r.milestones.map((ms: any) => ({ Text: ms.text.trim(), OwnerEmployeeID: ms.ownerEmployeeID, DueDate: ms.dueDate, Done: false })) })
                            .then(() => { added++; }).catch((e: any) => problems.push(`"${r.name}": ${this.errorText(e, 'not added')}.`));
                    }
                    this.rocksData[this.quarter] = (await axios.get(`${api}/api/rocks`, { params: { quarter: this.quarter } })).data;
                    this.done(`Added ${added} Rock${added == 1 ? '' : 's'} for ${this.teamLabel(this.team)}, ${this.quarterLabel(this.quarter)}.`,
                        { path: '/scoreboard', query: { view: 'rocks', team: this.team, quarter: this.quarter } }, 'Open Rocks', problems);
                } else if(this.kind == 'milestones') {
                    // The rock route replaces a rock's milestones, so each save re-reads the rock and keeps what's there
                    // (done or not), with the new steps merged in by due date
                    const fresh = (await axios.get(`${api}/api/rocks`, { params: { quarter: this.quarter } })).data;
                    let added = 0, rocks = 0;
                    for(const r of d.rocks.filter((x: any) => x.keep && x.milestones.length)) {
                        const rock = ((fresh && fresh.Rocks) || []).find((x: any) => x.ID == r.rockID);
                        if(!rock || !rock.CanEdit) { problems.push(`"${r.name}": not found, or no longer yours to edit.`); continue; }
                        const all = [ ...rock.Milestones.map((m: any) => ({ ID: m.ID, Text: m.Text, OwnerEmployeeID: m.OwnerEmployeeID, DueDate: m.DueDate, Done: !!m.Done })),
                            ...r.milestones.map((ms: any) => ({ Text: ms.text.trim(), OwnerEmployeeID: ms.ownerEmployeeID, DueDate: ms.dueDate, Done: false })) ];
                        const Milestones = all.map((m: any, i: number) => ({ m, i })).sort((a: any, b: any) => String(a.m.DueDate || '').localeCompare(String(b.m.DueDate || '')) || a.i - b.i).map((x: any) => x.m);
                        await axios.post(`${api}/api/rocks/rock`, { ID: rock.ID, Quarter: rock.Quarter, Team: rock.Team, Name: rock.Name, OwnerEmployeeID: rock.OwnerEmployeeID,
                            Status: rock.Status, DoneStatement: rock.DoneStatement, TeamRock: !!rock.TeamRock, Milestones })
                            .then(() => { added += r.milestones.length; rocks++; }).catch((e: any) => problems.push(`"${r.name}": ${this.errorText(e, 'not saved')}.`));
                    }
                    this.rocksData[this.quarter] = (await axios.get(`${api}/api/rocks`, { params: { quarter: this.quarter } })).data;
                    this.done(`Added ${added} milestone${added == 1 ? '' : 's'} to ${rocks} Rock${rocks == 1 ? '' : 's'}.`,
                        { path: '/scoreboard', query: { view: 'rocks', team: this.team, quarter: this.quarter } }, 'Open Rocks', problems);
                } else if(this.kind == 'tidyboard') {
                    let applied = 0;
                    const stepDates: any[] = [];
                    for(const a of d.actions.filter((x: any) => x.keep)) {
                        const c = a.card, label = `"${a.cardName}"`;
                        if(a.action == 'due' && a.checklistRef) { stepDates.push(a); continue; }
                        try {
                            if(a.action == 'due') {
                                // A card from a meeting is that meeting item: its date is set there (its access check applies)
                                const mi = c.MeetingItem;
                                if(mi) await axios.post(`${api}/api/meetingitems`, { ID: mi.ID, MeetingID: mi.MeetingID, Name: mi.Name, Description: mi.Description, Priority: mi.Priority,
                                    AssigneeEmployeeID: mi.AssigneeEmployeeID, Private: mi.Private, DueDate: a.dueDate });
                                else await axios.post(`${api}/api/companyboarditems`, { ...c, Date: a.dueDate, changedField: 'due date' });
                            } else if(a.action == 'move') {
                                await axios.post(`${api}/api/companyboarditems/changecolumn`, { Slug: this.board.slug, ID: c.ID, Column: a.toColumn });
                            } else if(a.action == 'done') {
                                if(c.MeetingItem) await axios.post(`${api}/api/meetingitems/complete`, { MeetingID: c.MeetingItem.MeetingID, ID: c.MeetingItem.ID, Completed: true });
                                else await axios.post(`${api}/api/companyboarditems`, { ...c, Checked: true });
                            } else {
                                const me = this.user.teamHubEmployee;
                                await axios.post(`${api}/api/rocks/rock`, { ID: null, Quarter: this.quarterOptions[0], Team: me.Team, Name: a.rockName.trim(), OwnerEmployeeID: me.ID,
                                    Status: 'on', DoneStatement: a.doneStatement.trim(), TeamRock: false, Milestones: [] });
                            }
                            applied++;
                        } catch(e: any) { problems.push(`${label}: ${this.errorText(e, 'not changed')}.`); }
                    }
                    if(stepDates.length) {
                        // Checklist steps: one save per card, from a fresh read so other changes to the checklist aren't lost
                        const fresh = (await axios.get(`${api}/api/companyboarditems/${encodeURIComponent(this.board.slug)}`).catch(() => ({ data: [] }))).data || [];
                        for(const cardID of [ ...new Set(stepDates.map((a: any) => a.cardID)) ]) {
                            const mine = stepDates.filter((a: any) => a.cardID == cardID);
                            const c = fresh.find((x: any) => Number(x.ID) == cardID);
                            const lists = c ? this.parseJSON(c.ChecklistJSON, []) : [];
                            let set = 0;
                            for(const a of mine) {
                                const [ , li, ii ] = a.checklistRef.split('.').map((n: string) => parseInt(n));
                                const it = lists[li] && (lists[li].items || [])[ii];
                                if(it && String(it.name || '').slice(0, 160) == a.stepName && !it.checked && !it.date) { it.date = a.dueDate; set++; }
                                else problems.push(`"${a.stepName}": the checklist changed since the Forge looked; not dated.`);
                            }
                            if(!set) continue;
                            await axios.post(`${api}/api/companyboarditems/checklist`, { id: c.ID, slug: this.board.slug, checklist: lists.map((l: any) => ({ name: l.name, items: l.items || [] })), updatedChecklist: 'dates' })
                                .then(() => { applied += set; }).catch((e: any) => problems.push(`"${mine[0].cardName}": ${this.errorText(e, 'checklist dates not saved')}.`));
                        }
                    }
                    this.done(`Applied ${applied} change${applied == 1 ? '' : 's'} to ${this.board.name}.`, null, '', problems);
                } else if(this.kind == 'planweek') {
                    let moved = 0;
                    for(const x of d.picks.filter((p: any) => p.keep && p.column != d.toColumn)) {
                        await axios.post(`${api}/api/companyboarditems/changecolumn`, { Slug: this.board.slug, ID: x.card.ID, Column: d.toColumn })
                            .then(() => { moved++; }).catch((e: any) => problems.push(`"${x.name}": ${this.errorText(e, 'not moved')}.`));
                    }
                    this.done(`Moved ${moved} card${moved == 1 ? '' : 's'} to ${d.toColumn}.`, null, '', problems);
                } else if(this.kind == 'sortinbox') {
                    // Rename in the Inbox, move onto the board, then set the due date on the new card
                    const dated: any[] = [];
                    let added = 0;
                    for(const x of d.items.filter((i: any) => i.keep)) {
                        try {
                            if(x.name.trim() != x.raw.Name) await axios.post(`${api}/api/inbox`, { ID: x.inboxID, Name: x.name.trim(), Description: x.raw.Description || '' });
                            // The card keeps its ID on the board
                            await axios.post(`${api}/api/companyboarditems/move`, { Slug: this.inboxSlug, ID: x.inboxID, TargetSlug: this.board.slug, Column: x.column });
                            if(x.dueDate) dated.push({ id: Number(x.inboxID), date: x.dueDate });
                            added++;
                        } catch(e: any) { problems.push(`"${x.name}": ${this.errorText(e, 'not added')}.`); }
                    }
                    if(dated.length) {
                        const cards = (await axios.get(`${api}/api/companyboarditems/${encodeURIComponent(this.board.slug)}`)).data || [];
                        for(const dd of dated) {
                            const c = cards.find((x: any) => Number(x.ID) == dd.id);
                            if(!c) continue;
                            await axios.post(`${api}/api/companyboarditems`, { ...c, TagsJSON: this.parseJSON(c.TagsJSON, []), Date: dd.date, changedField: 'due date' })
                                .catch((e: any) => problems.push(`"${c.Name}": due date not set (${this.errorText(e, 'error')}).`));
                        }
                    }
                    await this.loadInbox().catch(() => null);
                    this.done(`Added ${added} card${added == 1 ? '' : 's'} to ${this.board.name}.`, null, '', problems);
                } else if(this.forMeeting) {
                    // Into the meeting's Issues column, through the meeting items route (its access check applies)
                    const rows = this.kind == 'prepl10'
                        ? d.issues.filter((i: any) => i.keep).map((i: any) => ({ Name: i.name.trim(), Description: i.description, Priority: i.priority, AssigneeEmployeeID: i.assigneeEmployeeID }))
                        : d.suggestions.filter((x: any) => x.keep).map((x: any) => ({ Name: x.title, Description: x.detail, Priority: 'med', AssigneeEmployeeID: null }));
                    let added = 0;
                    for(const row of rows) {
                        await axios.post(`${api}/api/meetingitems`, { ID: null, MeetingID: this.meeting.id, Column: this.facts.issuesColumn, ...row })
                            .then(() => { added++; }).catch((e: any) => problems.push(`"${row.Name}": ${this.errorText(e, 'not added')}.`));
                    }
                    this.done(`Added ${added} Issue${added == 1 ? '' : 's'} to ${this.facts.issuesColumn} on ${this.meeting.name}.`, null, '', problems);
                } else if(this.kind == 'improvemetrics') {
                    // Name, definition, unit and a new owner change; the goal in effect this week is sent back as it is,
                    // so the goal history isn't touched
                    const week = mondayOf(new Date());
                    const fresh = (await axios.get(`${api}/api/scoreboard`, { params: { week } })).data;
                    let updated = 0;
                    for(const m of d.metrics.filter((x: any) => x.keep && this.metricChanged(x))) {
                        const metric = ((fresh && fresh.Metrics) || []).find((x: any) => x.ID == m.metricID);
                        const g = metric && goalAt(metric, week);
                        if(!metric || !g) { problems.push(`"${m.old.name}": not found any more.`); continue; }
                        await axios.post(`${api}/api/scoreboard/metric`, { ID: metric.ID, Team: metric.Team, Name: m.name.trim(), OwnerEmployeeID: m.ownerEmployeeID, Unit: m.unit.trim(),
                            Definition: m.definition.trim(), Cmp: g.Cmp, Goal: g.Goal, GoalMax: g.Cmp == 'range' ? g.GoalMax : null, Week: week, GoalWhen: 'now' })
                            .then(() => { updated++; }).catch((e: any) => problems.push(`"${m.old.name}": ${this.errorText(e, 'not saved')}.`));
                    }
                    this.scoreboard = (await axios.get(`${api}/api/scoreboard`, { params: { week } })).data;
                    this.done(`Updated ${updated} metric${updated == 1 ? '' : 's'} on the ${this.teamLabel(this.team)} scorecard.`,
                        { path: '/scoreboard', query: { team: this.team } }, 'Open the scorecard', problems);
                } else if(this.kind == 'improve') {
                    // Only the name and done statement change: everything else is sent back as it is now (the route
                    // replaces milestones, so they're re-read and passed through untouched)
                    const fresh = (await axios.get(`${api}/api/rocks`, { params: { quarter: this.quarter } })).data;
                    let updated = 0;
                    for(const r of d.rocks.filter((x: any) => x.keep && this.rockChanged(x))) {
                        const rock = ((fresh && fresh.Rocks) || []).find((x: any) => x.ID == r.rockID);
                        if(!rock || !rock.CanEdit) { problems.push(`"${r.oldName}": not found, or no longer yours to edit.`); continue; }
                        await axios.post(`${api}/api/rocks/rock`, { ID: rock.ID, Quarter: rock.Quarter, Team: rock.Team, Name: r.name.trim(), OwnerEmployeeID: rock.OwnerEmployeeID,
                            Status: rock.Status, DoneStatement: r.doneStatement.trim(), TeamRock: !!rock.TeamRock,
                            Milestones: rock.Milestones.map((m: any) => ({ ID: m.ID, Text: m.Text, OwnerEmployeeID: m.OwnerEmployeeID, DueDate: m.DueDate, Done: !!m.Done })) })
                            .then(() => { updated++; }).catch((e: any) => problems.push(`"${r.oldName}": ${this.errorText(e, 'not saved')}.`));
                    }
                    this.rocksData[this.quarter] = (await axios.get(`${api}/api/rocks`, { params: { quarter: this.quarter } })).data;
                    this.done(`Updated ${updated} Rock${updated == 1 ? '' : 's'}.`,
                        { path: '/scoreboard', query: { view: 'rocks', team: this.team, quarter: this.quarter } }, 'Open Rocks', problems);
                } else if(this.kind == 'training') {
                    const body = { ID: null, Name: d.name.trim(), Date: d.date, Type: d.type, Station: d.type == 'On-site' ? (d.station || null) : null,
                        ReviewingText: d.reviewingText || null, ProcessesJSON: d.processSlugs.map((slug: string) => ({ slug })) };
                    await axios.post(`${api}/api/classroom`, body);
                    // The create route doesn't return the ID; find the session just made
                    const list = (await axios.get(`${api}/api/classroom`).catch(() => ({ data: [] }))).data || [];
                    const made = list.filter((c: any) => c.Name == body.Name && String(c.Date || '').slice(0, 10) == body.Date).sort((a: any, b: any) => b.ID - a.ID)[0];
                    this.done(`Created "${body.Name}" for ${this.shortDate(body.Date)}.`, made ? '/classroom/' + made.ID : '/classrooms', made ? 'Open the session' : 'Open Training');
                } else if(this.kind == 'skills') {
                    await this.applySkills(api, d, problems);
                } else if(this.kind == 'announcement') {
                    let sent = 0;
                    for(const loc of d.locations) {
                        await axios.post(`${api}/api/announcements`, { Text: d.text.trim(), Location: loc })
                            .then(() => { sent++; }).catch((e: any) => problems.push(`${loc}: ${this.errorText(e, 'not posted')}.`));
                    }
                    this.done(`Posted to ${sent} location${sent == 1 ? '' : 's'}${sent ? ' and texted the team there' : ''}.`, '/announcements', 'Open Announcements', problems);
                } else if(this.kind == 'event') {
                    await axios.post(`${api}/api/teamevents`, { Name: d.name.trim(), Description: d.description, Date: d.date, StartTime: d.startTime || null,
                        TimeZone: d.startTime ? browserTimeZone() : null, DurationMinutes: d.durationMinutes, Location: d.location });
                    let sent = 0;
                    if(d.sendOut) {
                        for(const loc of d.sendLocations) {
                            await axios.post(`${api}/api/announcements`, { Text: d.announcementText.trim(), Location: loc })
                                .then(() => { sent++; }).catch((e: any) => problems.push(`${loc}: ${this.errorText(e, 'announcement not posted')}.`));
                        }
                    }
                    this.done(`Added "${d.name.trim()}" to Celebrations${sent ? ` and sent it to ${sent} location${sent == 1 ? '' : 's'}` : ''}.`, '/celebrations', 'Open Celebrations', problems);
                }
            } catch(e: any) {
                this.applyError = this.errorText(e, 'That didn\'t save. Please try again.');
            }
            this.busy = false;
            this.confirmTexts = false;
        },
        // Skills: columns are skills ({name, id}), rows are people with { colId: level }. The routes don't return IDs,
        // so each step re-reads before the next.
        async applySkills(api: string, d: any, problems: string[]) {
            let matrixID = d.matrixID;
            const newID = (i: number) => (Date.now() + i).toString(36);
            if(!matrixID) {
                const name = d.newMatrixName.trim();
                await axios.post(`${api}/api/skillsmatrices`, { Name: name, ColumnsJSON: d.addSkills.map((s: string, i: number) => ({ name: s, id: newID(i) })) });
                const all = (await axios.get(`${api}/api/skillsmatrices`)).data || [];
                const made = all.filter((m: any) => m.Name == name).sort((a: any, b: any) => b.ID - a.ID)[0];
                if(!made) throw new Error('The new matrix wasn\'t found after saving.');
                matrixID = made.ID;
            } else {
                const m = await this.matrixWithRows(matrixID);
                const cols = this.parseJSON(m.ColumnsJSON, []);
                const add = d.addSkills.filter((s: string) => !cols.some((c: any) => c.name.toLowerCase() == s.toLowerCase()));
                if(add.length) {
                    await axios.post(`${api}/api/skillsmatrices`, { ID: matrixID, Name: m.Name, ColumnsJSON: [ ...cols, ...add.map((s: string, i: number) => ({ name: s, id: newID(i) })) ] });
                }
            }
            let m = await this.matrixWithRows(matrixID);
            const newPeople = d.addPeople.filter((p: string) => !m.rows.some((r: any) => r.Name.toLowerCase() == p.toLowerCase()));
            for(const p of newPeople) {
                await axios.post(`${api}/api/skillsmatrices/row`, { Name: p, ColumnsJSON: {}, MatrixID: matrixID });
            }
            if(newPeople.length) m = await this.matrixWithRows(matrixID);
            const cols = this.parseJSON(m.ColumnsJSON, []);
            const byRow: any = {};
            let rated = 0;
            for(const r of d.ratings.filter((x: any) => x.keep)) {
                const row = m.rows.find((x: any) => x.Name.toLowerCase() == r.person.toLowerCase());
                const col = cols.find((c: any) => c.name.toLowerCase() == r.skill.toLowerCase());
                if(!row || !col) { problems.push(`${r.person} / ${r.skill}: not on the matrix, so not rated.`); continue; }
                byRow[row.ID] = byRow[row.ID] || { row, levels: { ...this.parseJSON(row.ColumnsJSON, {}) } };
                byRow[row.ID].levels[col.id] = r.level;
                rated++;
            }
            for(const { row, levels } of Object.values(byRow) as any[]) {
                await axios.post(`${api}/api/skillsmatrices/row`, { ID: row.ID, Name: row.Name, ColumnsJSON: levels });
            }
            const parts = [];
            if(d.addSkills.length) parts.push(`${d.addSkills.length} skill${d.addSkills.length == 1 ? '' : 's'}`);
            if(newPeople.length) parts.push(`${newPeople.length} ${newPeople.length == 1 ? 'person' : 'people'}`);
            if(rated) parts.push(`${rated} rating${rated == 1 ? '' : 's'}`);
            this.matrices = (await axios.get(`${api}/api/skillsmatrices`)).data || [];
            this.done(`Updated ${m.Name}: ${parts.join(', ') || 'no changes'}.`, '/skills-matrix/' + matrixID, 'Open the matrix', problems);
        }
    }
});
</script>

<style scoped>
.ft-modal { width: 860px; max-width: calc(100vw - 32px); margin: 0 auto !important; text-align: left; }
/* Side panel (modal on a wide screen): fixed on the right under the page bar, scrolls on its own */
.ft-dock-wrap { position: fixed; right: 0; bottom: 0; width: var(--ft-dock-w); z-index: 950; }
.ft.ft-docked { position: relative; height: 100%; box-sizing: border-box; overflow-y: auto; margin: 0; border-radius: 0; border: 0; border-left: 1px solid #eadfd5;
    box-shadow: -16px 0 36px -20px rgba(44, 41, 38, .35); padding: 18px 18px 22px; text-align: left; animation: ft-dock-in .18s ease-out; }
@keyframes ft-dock-in { from { transform: translateX(24px); } to { transform: none; } }
@media (prefers-reduced-motion: reduce) { .ft.ft-docked { animation: none; } }
/* The panel is narrow even on a wide screen: the same layout as the narrow-screen rules below */
.ft-docked .ft-grid-metric, .ft-docked .ft-grid-rock, .ft-docked .ft-grid-training, .ft-docked .ft-grid-event { grid-template-columns: 1fr 1fr; }
.ft-docked .ft-grid-metric > .ft-strong, .ft-docked .ft-grid-rock > .ft-strong, .ft-docked .ft-grid-training > .ft-strong, .ft-docked .ft-grid-event > .ft-strong { grid-column: 1 / -1; }
.ft-docked .ft-grid-ms { grid-template-columns: minmax(0, 1fr) 128px 24px; }
.ft-docked .ft-grid-ms > .ft-ms-text { grid-column: 1 / -1; }
.ft-docked .ft-grid-ms select, .ft-docked .ft-grid-ms input[type=date] { font-size: 14px; padding: 5px 6px; }
.ft-docked .ft-grid-rating { grid-template-columns: 1fr 1fr; }
.ft-docked .ft-grid-rating select { grid-column: 1 / -1; }
.ft-docked .ft-row select { min-width: 0; width: 100%; }
.ft-docked .ft-row label { flex: 1 1 100%; }
.ft-close { position: absolute; top: 10px; right: 12px; width: 32px; height: 32px; border: none; background: none; font-size: 26px; line-height: 1; color: #8a7f75; cursor: pointer; padding: 0; }
.ft-close:hover { color: #1f1712; }
.ft-facts { background: #faf6f2; border-radius: 12px; padding: 12px 16px; margin-bottom: 12px; display: grid; gap: 6px; }
.ft-facts ul { margin: 0; padding-left: 18px; display: grid; gap: 3px; font-size: 14.5px; }
.ft-stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; margin-bottom: 4px; }
.ft-stat { background: #fff; border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 10px 12px; display: grid; gap: 2px; }
.ft-stat strong { font-size: 20px; color: #1f1712; font-variant-numeric: tabular-nums; }
.ft-stat span { font-size: 12.5px; color: #6f655c; line-height: 1.3; }
.ft-row-tight { margin: 4px 0 0; gap: 10px; align-items: center; }
.ft-tag { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; border-radius: 999px; padding: 2px 9px; background: #f3ece5; color: #6f655c; }
.ft-tag-rock { background: #eaf0f8; color: #1d4f91; } .ft-tag-scorecard { background: #fdf0e6; color: #c2410c; } .ft-tag-todo { background: #edf7ef; color: #2c7a4b; } .ft-tag-other { background: #f1eefa; color: #5b3fa3; }
.ft-pri { width: auto !important; min-width: 120px; }
.ft-sg-detail { display: block; font-size: 14px; color: #5c5148; margin-top: 2px; }
.ft-was { background: #f7f3ef; border-radius: 10px; padding: 9px 12px; font-size: 14px; color: #5c5148; line-height: 1.45; }
.ft-was-name { font-weight: 700; color: #3a2f27; }
.ft { margin: 22px 0; background: #fff; border: 1px solid var(--ai-border-soft); border-radius: 16px; padding: 20px 22px; box-shadow: var(--ai-shadow-card); }
.ft-head { display: flex; gap: 14px; align-items: center; margin-bottom: 14px; }
.ft-head h2 { margin: 0; font-size: 20px; color: #1f1712; }
.ft-head p { margin: 2px 0 0; color: #6f655c; font-size: 14px; }
.ft-icon { width: 44px; height: 44px; border-radius: 12px; background: var(--ai-grad); display: flex; align-items: center; justify-content: center; flex: none; box-shadow: var(--ai-shadow); }
.ft-icon svg { width: 22px; height: 22px; fill: #fff; }
.ft-variants { display: inline-flex; flex-wrap: wrap; gap: 2px; padding: 3px; background: #f5f0eb; border: 1px solid #ece3da; border-radius: 10px; margin: 0 0 14px; }
.ft-variant { border: none; background: transparent; border-radius: 8px; padding: 6px 13px; font: inherit; font-size: 13.5px; font-weight: 600; color: #6f655c; cursor: pointer; }
.ft-variant:hover:not(:disabled) { color: #2b221b; }
.ft-variant-on { background: #fff; color: #2b221b; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); }
.ft-variant:disabled { cursor: default; }
.ft-kinds { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; margin-bottom: 14px; }
.ft-kind { text-align: left; background: #faf7f4; border: 1px solid #efe6dd; border-radius: 10px; padding: 9px 12px; cursor: pointer; font: inherit; color: #2a2a2a; }
.ft-kind:hover { border-color: #fdba74; }
.ft-kind-on { background: #fff7ef; border-color: var(--ai-accent); box-shadow: 0 0 0 2px rgba(234, 88, 12, .12); }
.ft-kind-name { display: block; font-weight: 700; font-size: 14.5px; }
.ft-kind-sub { display: block; font-size: 12px; color: #8a7f75; }
.ft-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 10px; }
.ft-row label { font-size: 12px; font-weight: 700; color: #5c4636; display: flex; flex-direction: column; gap: 4px; }
.ft-row select { margin: 0; min-width: 200px; }
.ft-describe { position: relative; }
.ft-describe textarea { width: 100%; box-sizing: border-box; padding-right: 48px; margin: 0; }
.ft-mic { position: absolute; right: 8px; top: 8px; }
.ft-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 10px; }
.ft-actions .ft-hint { margin-right: auto; }
.ft-hint, .ft-why, .ft-meta { font-size: 13px; color: #8a7f75; }
.ft-meta { margin-bottom: 4px; color: #5c4636; }
.ft-why { margin-top: 4px; font-style: italic; }
.ft-summary { background: var(--ai-grad-soft); border-radius: 10px; padding: 10px 14px; color: #3d332b; font-size: 14.5px; margin: 0 0 12px; }
.ft-list { display: grid; gap: 8px; }
.ft-list input[type=text], .ft-list input[type=number], .ft-list input[type=date], .ft-list input[type=time], .ft-list select, .ft-list textarea { margin: 0; width: 100%; box-sizing: border-box; min-width: 0; padding: 5px 10px; }
/* The app's global inputs are 220px wide with side padding; checkboxes here stay checkbox-sized */
.ft input[type=checkbox] { width: 18px; height: 18px; min-width: 18px; max-width: 18px; padding: 0; margin: 0; flex: none; }
.ft-item { display: flex; gap: 10px; align-items: flex-start; border: 1px solid #efe6dd; border-radius: 10px; padding: 10px 12px; }
.ft-item > input[type=checkbox] { margin-top: 9px; }
.ft-item-body { flex: 1; min-width: 0; display: grid; gap: 6px; }
.ft-item-tight { padding: 6px 12px; align-items: center; }
.ft-item-tight > input[type=checkbox] { margin-top: 0; }
.ft-off { opacity: .5; }
.ft-rockpick { margin-bottom: 10px; }
.ft-rockpick .ft-item { cursor: pointer; }
.ft-rockpick-name { flex: 1; min-width: 0; font-weight: 600; font-size: 14.5px; }
.ft-existing { font-size: 14px; color: #3d332b; padding-left: 2px; }
.ft-existing-done { color: #8a7f75; text-decoration: line-through; }
.ft-grid { display: grid; gap: 6px; align-items: center; }
.ft-grid-metric { grid-template-columns: minmax(140px, 2fr) minmax(120px, 1.3fr) 105px 90px auto 70px; }
.ft-grid-rock { grid-template-columns: minmax(160px, 3fr) minmax(120px, 1.2fr); }
.ft-grid-ms { grid-template-columns: minmax(140px, 3fr) minmax(110px, 1.2fr) 150px 28px; }
/* Milestone text wraps and grows with its content instead of being cut off */
.ft-grid-ms > .ft-ms-text { font-size: 15px; resize: none; field-sizing: content; height: auto; min-height: 34px; line-height: 1.35; overflow: hidden; }
.ft-grid-training { grid-template-columns: minmax(160px, 3fr) 140px 160px; }
.ft-grid-rating { flex: 1; grid-template-columns: 1fr 1fr 190px; }
.ft-grid-event { grid-template-columns: minmax(160px, 3fr) 150px 130px 120px 150px; }
.ft-strong { font-weight: 700; }
.ft-unit { max-width: 80px; }
.ft-label { font-size: 12px; font-weight: 700; color: #5c4636; margin-top: 4px; }
.ft-check { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; }
.ft-check input { margin: 0; }
.ft-loc { background: #faf7f4; border: 1px solid #efe6dd; border-radius: 16px; padding: 4px 10px; }
.ft-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ft-chip { display: inline-flex; align-items: center; gap: 4px; background: #fff3e8; color: #7a3410; border-radius: 14px; padding: 3px 4px 3px 10px; font-size: 13px; }
.ft-x { border: 0; background: none; cursor: pointer; font-size: 17px; line-height: 1; color: #a0958a; padding: 0 4px; }
.ft-x:hover { color: #b91c1c; }
.ft-add { font-size: 13px; color: var(--ai-accent-deep); cursor: pointer; }
.ft-pick { border: 1px solid #efe6dd; border-radius: 8px; max-height: 200px; overflow: auto; }
.ft-pick-row { padding: 7px 10px; cursor: pointer; font-size: 14px; border-bottom: 1px solid #f6efe8; }
.ft-pick-row:hover { background: #fff5ec; }
.ft-inline { display: inline-block; width: auto !important; min-width: 220px; margin-left: 6px !important; }
.ft-revise { margin-top: 14px; border-top: 1px solid #f1e8df; padding-top: 12px; }
.ft-revise textarea { width: 100%; box-sizing: border-box; margin: 0; }
.ft-revise button.ai-btn { border: 0; font: inherit; }
.ft-revise button:disabled { opacity: .5; pointer-events: none; }
.ft-warn { margin-top: 12px; background: #fff8e6; border: 1px solid #f3d38a; color: #6b4d00; border-radius: 8px; padding: 8px 12px; font-size: 14px; }
.ft-error { margin: 10px 0; background: #fdecec; border: 1px solid #f3c4c4; color: #8a1f1f; border-radius: 8px; padding: 8px 12px; font-size: 14px; }
.ft-loading { display: flex; align-items: center; gap: 12px; padding: 18px 4px; color: #5c4636; }
.ft-orb { width: 26px; height: 26px; border-radius: 50%; background: conic-gradient(from 0deg, #f59e0b, #f97316, #ea580c, #f59e0b);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 4px)); mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 4px));
    animation: ft-spin 1.1s linear infinite; }
@keyframes ft-spin { to { transform: rotate(360deg); } }
.ft-done { padding: 6px 0; }
.ft-done-msg { font-size: 16px; font-weight: 600; color: #1f5f3a; }
.ft-done .ai-btn { text-decoration: none; }
.ft-done button.ai-btn { border: 0; font: inherit; }
.ft-fit { resize: vertical; }
@media (max-width: 760px) {
    .ft { padding: 16px 14px; }
    .ft-grid-metric, .ft-grid-rock, .ft-grid-training, .ft-grid-event { grid-template-columns: 1fr 1fr; }
    .ft-grid-metric > .ft-strong, .ft-grid-rock > .ft-strong, .ft-grid-training > .ft-strong, .ft-grid-event > .ft-strong { grid-column: 1 / -1; }
    .ft-grid-ms { grid-template-columns: minmax(0, 1fr) 128px 24px; }
    .ft-grid-ms > .ft-ms-text { grid-column: 1 / -1; }
    .ft-grid-ms select, .ft-grid-ms input[type=date] { font-size: 14px; padding: 5px 6px; }
    .ft-grid-rating { grid-template-columns: 1fr 1fr; }
    .ft-grid-rating select { grid-column: 1 / -1; }
    .ft-row select { min-width: 0; width: 100%; }
    .ft-row label { flex: 1 1 100%; }
}
</style>
